import { localizeDateNames } from "@/lib/dateNames";
import { detailOr } from "@/lib/apiErrorMessage";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useApp } from "@/context/AppContext";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import PageHeader from "@/components/PageHeader";
import DownloadPdfButton from "@/components/DownloadPdfButton";
import DownloadXlsxButton from "@/components/DownloadXlsxButton";
import { useNavigate } from "react-router-dom";
import {
    Download, ArrowRight, BarChart3, Boxes, Truck, ClipboardCheck,
    Building2, Briefcase, Trash2, FileText, ChevronRight,
} from "lucide-react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer, Cell,
} from "recharts";

const kindLabel = (k) => i18n.t(`reports.kind.${k.key}`, k.label);

const REPORT_KINDS = [
    { key: "inventory", label: "Inventory", icon: Boxes, source: "/inventory", corporateOnly: false },
    { key: "purchasing", label: "Purchasing", icon: Truck, source: "/deliveries", corporateOnly: false },
    { key: "waste", label: "Waste", icon: Trash2, source: "/production-review", corporateOnly: false },
    { key: "daily-checks", label: "Daily Checks", icon: ClipboardCheck, source: "/daily-checks", corporateOnly: false },
    { key: "suppliers", label: "Suppliers", icon: Building2, source: "/suppliers", corporateOnly: false },
    { key: "corporate-comparison", label: "Corporate", icon: Briefcase, source: "/workflow", corporateOnly: true },
];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function isoBackDays(days) {
    const d = new Date(); d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
}
// Report Date Filters · "Current month" sets From = 1st of this month
// in the viewer's existing local calendar (so the chef sees the same
// month as their on-screen calendar) and leaves `to` to be set with
// the existing `todayISO()` so the end bound stays identical to the
// current behaviour of other filter actions.
function firstOfCurrentMonthISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}-01`;
}

function Stat({ label, value, sub, testId, tone }) {
    const valColor = tone === "good" ? "text-emerald-700"
        : tone === "warn" ? "text-amber-700"
        : tone === "bad" ? "text-red-700" : "text-neutral-900";
    return (
        <div className="kop-card p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
            <div className={`mt-2 font-display text-3xl font-extrabold tracking-tight ${valColor}`}>{value}</div>
            {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
        </div>
    );
}

function Table({ columns, rows, testId, emptyText = i18n.t("reports.noData", "No data.") }) {
    if (!rows || rows.length === 0) {
        return <div className="kop-card p-5 text-sm text-neutral-500" data-testid={`${testId}-empty`}>{emptyText}</div>;
    }
    return (
        <div className="kop-card ms-rt-wrap">
            <table className="w-full text-sm ms-rt" data-testid={testId}>
                <thead className="bg-neutral-50">
                    <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                        {columns.map((c) => (
                            <th key={c.key} className={`px-5 py-3 font-semibold ${c.right ? "text-right" : ""}`}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                    {rows.map((row, i) => (
                        <tr key={row.id || row.key || i}>
                            {columns.map((c) => (
                                <td key={c.key} className={`px-5 py-3 ${c.right ? "text-right tabular-nums" : ""} ${c.bold ? "font-medium" : "text-neutral-700"}`}>
                                    {c.format ? c.format(row[c.key], row) : (row[c.key] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const FMT_NUM = (v) =>
    Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FMT_EUR = (v) => (v == null ? "—" : `€ ${FMT_NUM(v)}`);
const FMT_EUR_OR_NO_PREV = (v) =>
    v == null ? i18n.t("reports.noPreviousInventory", "No previous inventory") : `€ ${FMT_NUM(v)}`;
const FMT_EUR_OR_NO_CLOSING = (v) =>
    v == null ? i18n.t("reports.noInventoryInPeriod", "No inventory in period") : `€ ${FMT_NUM(v)}`;
const FMT_EUR_OR_NA = (v) =>
    v == null ? i18n.t("reports.unavailable", "Unavailable") : `€ ${FMT_NUM(v)}`;
const FMT_PCT = (v) => `${Number(v || 0).toFixed(1)}%`;

export default function Reports() {
    const { user, restaurantId } = useApp();
    const { isFeatureEnabled } = useResolvedConfig();
    const wasteEnabled = isFeatureEnabled("waste_report");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isCorporate = user?.role === "corporate";
    const [kind, setKind] = useState("inventory");
    // R1.2 · Runtime date initialization. Lazy `useState` initializer so
    // the dates are computed on first mount, not module load, and never
    // captured as a stale constant.
    const [from, setFrom] = useState(() => isoBackDays(29));
    const [to, setTo] = useState(() => todayISO());
    const [data, setData] = useState(null);
    // Track the kind the current `data` payload belongs to. Prevents the
    // previous kind's shape from leaking into a new tab's initial render
    // (which crashed WorkflowReport with 'undefined.map').
    const [dataKind, setDataKind] = useState(null);
    const [loading, setLoading] = useState(false);
    // Waste-only filters (dish substring + recipe category).
    const [wasteDish, setWasteDish] = useState("");
    const [wasteCategory, setWasteCategory] = useState("");
    const [wasteCategories, setWasteCategories] = useState([]);

    const visibleKinds = useMemo(
        () => REPORT_KINDS.filter((k) =>
            (!k.corporateOnly || isCorporate)
            && (k.key !== "waste" || wasteEnabled)),
        [isCorporate, wasteEnabled]
    );

    const params = useMemo(() => {
        const p = { from, to };
        if (isCorporate && kind !== "corporate-comparison" && restaurantId) {
            p.restaurant_id = restaurantId;
        }
        if (kind === "waste") {
            if (wasteDish.trim()) p.dish = wasteDish.trim();
            if (wasteCategory) p.category = wasteCategory;
        }
        return p;
    }, [from, to, isCorporate, kind, restaurantId, wasteDish, wasteCategory]);

    useEffect(() => {
        if (!restaurantId && kind !== "corporate-comparison") return;
        // Clear the previous payload synchronously — see also the dataKind
        // guard below which prevents any leftover render from using the old
        // shape for the new kind.
        setData(null);
        setDataKind(null);
        setLoading(true);
        const forKind = kind;
        api.get(`/reports/${kind}`, { params })
            .then(({ data }) => { setData(data); setDataKind(forKind); })
            .catch(() => { setData(null); setDataKind(null); })
            .finally(() => setLoading(false));
    }, [kind, params, restaurantId]);

    // Load the canonical category list once we land on the Waste tab.
    useEffect(() => {
        if (kind !== "waste" || wasteCategories.length) return;
        api.get("/recipes/categories")
            .then(({ data }) => setWasteCategories(data || []))
            .catch(() => {});
    }, [kind, wasteCategories.length]);

    const currentKind = REPORT_KINDS.find((k) => k.key === kind);

    const downloadCSV = async (overrideParams) => {
        const qs = new URLSearchParams(overrideParams || params).toString();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/${kind}/csv?${qs}`;
        window.location.href = url;
    };
    const downloadPDF = async () => {
        // Only the waste report ships a PDF export today.
        const qs = new URLSearchParams(params).toString();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/waste/pdf?${qs}`;
        window.location.href = url;
    };

    // Corporate provides its own compact header + filters. When the
    // Corporate tab is active we suppress the outer "Reports" title and
    // the outer date/quick strip to avoid a duplicate heading and a
    // duplicate filter area. Actions (Open source / CSV / Excel) are
    // hoisted so Corporate can render them in its own compact header.
    const isCorpKind = kind === "corporate-comparison";
    const buildActionsNode = (overrideParams, opts) => {
        const effParams = overrideParams || params;
        const omitOpenSource = opts && opts.omitOpenSource;
        return (
        <div className="flex flex-wrap gap-2">
            {!omitOpenSource && (
            <button
                data-testid="report-open-source"
                onClick={() => navigate(currentKind?.source || "/workflow")}
                className="h-10 px-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50"
            >
                {t("reports.openSourceModule", "Open source module")} <ArrowRight className="h-4 w-4" />
            </button>
            )}
            <button
                data-testid="report-download-csv"
                onClick={() => downloadCSV(effParams)}
                className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
            >
                <Download className="h-4 w-4" /> {t("reports.csv", "CSV")}
            </button>
            <DownloadXlsxButton
                endpoint={`/reports/${kind}/xlsx`}
                params={effParams}
                label="Excel"
            />
            {kind === "waste" && (
                <button
                    data-testid="report-download-pdf"
                    onClick={downloadPDF}
                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50"
                >
                    <FileText className="h-4 w-4" /> {t("reports.pdf", "PDF")}
                </button>
            )}
        </div>
        );
    };
    const actionsNode = buildActionsNode();

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
            {!isCorpKind && (
                <PageHeader
                    title={t("reports.title", "Reports")}
                    subtitle={t("reports.subtitle", "Read-only insights generated from your operational data.")}
                    stackOnMobile
                    action={actionsNode}
                />
            )}

            {/* Filters — hidden on Corporate; Corporate ships its own
                single compact filter area (chips + date/grouping/comparison). */}
            {!isCorpKind && (
                <div className="mb-6 flex flex-wrap items-end gap-3" data-testid="report-filters">
                    <div>
                        <label className="block text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-1">{t("reports.from", "From")}</label>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                            data-testid="report-filter-from"
                            className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-1">{t("reports.to", "To")}</label>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                            data-testid="report-filter-to"
                            className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white" />
                    </div>
                    <div className="flex gap-1">
                        <button type="button"
                            data-testid="report-quick-current-month"
                            onClick={() => { setFrom(firstOfCurrentMonthISO()); setTo(todayISO()); }}
                            className="h-10 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                            {i18n.t("reports.currentMonth", "Current month")}
                        </button>
                    </div>
                </div>
            )}

            {/* Kind tabs */}
            <div className="mb-8 flex flex-wrap gap-2" data-testid="report-tabs">
                {visibleKinds.map((k) => (
                    <button key={k.key}
                        data-testid={`report-tab-${k.key}`}
                        onClick={() => setKind(k.key)}
                        className={`h-10 px-4 inline-flex items-center gap-2 rounded-md text-sm font-semibold transition ${
                            kind === k.key
                                ? "bg-neutral-900 text-white"
                                : "border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                    >
                        <k.icon className="h-4 w-4" /> {kindLabel(k)}
                    </button>
                ))}
            </div>

            {kind === "waste" && (
                <div className="mb-6 flex flex-wrap items-end gap-3"
                     data-testid="waste-filters">
                    <div>
                        <label className="block text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-1">{t("reports.dish", "Dish")}</label>
                        <input
                            data-testid="waste-filter-dish"
                            value={wasteDish}
                            onChange={(e) => setWasteDish(e.target.value)}
                            placeholder={t("reports.filterByDish", "Filter by dish name…")}
                            className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white w-64" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wide font-semibold text-neutral-600 mb-1">{t("reports.category", "Category")}</label>
                        <select
                            data-testid="waste-filter-category"
                            value={wasteCategory}
                            onChange={(e) => setWasteCategory(e.target.value)}
                            className="h-10 px-3 border border-neutral-300 rounded-md text-sm bg-white">
                            <option value="">{t("reports.allCategories", "All categories")}</option>
                            {wasteCategories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                    {(wasteDish || wasteCategory) && (
                        <button
                            data-testid="waste-filter-clear"
                            onClick={() => { setWasteDish(""); setWasteCategory(""); }}
                            className="h-10 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                            {t("reports.clear", "Clear")}
                        </button>
                    )}
                </div>
            )}

            {loading && (
                <div className="kop-card p-6 text-sm text-neutral-500" data-testid="report-loading">{t("common.loading", "Loading…")}</div>
            )}

            {!loading && data && dataKind === kind &&
              ["purchasing","inventory","waste","overview","daily-checks"].includes(kind) && (
                <div className="mb-3 flex justify-end">
                    <DownloadPdfButton
                        endpoint={`/reports/${kind}/pdf`}
                        params={{ ...(restaurantId ? {restaurant_id: restaurantId} : {}),
                                    from, to }} />
                </div>
              )}

            {!loading && data && dataKind === kind && kind === "inventory" && <InventoryReport data={data} />}
            {!loading && data && dataKind === kind && kind === "purchasing" && <PurchasingReport data={data} isCorporate={isCorporate} />}
            {!loading && data && dataKind === kind && kind === "waste" && <WasteReport data={data} isCorporate={isCorporate} />}
            {!loading && data && dataKind === kind && kind === "daily-checks" && <DailyChecksReport data={data} />}
            {!loading && data && dataKind === kind && kind === "suppliers" && <SuppliersReport data={data} />}
            {!loading && data && dataKind === kind && kind === "corporate-comparison" && <CorporateReport data={data} renderActions={(overrideParams) => buildActionsNode(overrideParams, { omitOpenSource: true })} />}

            <div className="mt-12 text-[11px] text-neutral-400 italic">
                {t("reports.readOnlyNote", "Reports are read-only. To fix an issue, navigate to the source operational module.")}
            </div>
        </div>
    );
}

function InventoryReport({ data }) {
    // Message 405 — explicit NET / VAT / TOTAL presentation on all
    // inventory KPIs. Values come from the authoritative accounting
    // dataset via /reports/inventory. Null-vs-zero remains preserved:
    // no previous inventory → "No previous inventory" (not €0.00).
    const { t } = useTranslation();
    return (
        <div data-testid="report-inventory">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TripleStat
                    testId="inv-opening"
                    label={t("reports.inv.opening", "Opening inventory")}
                    sub={data.opening_inventory_month || data.opening_date || "—"}
                    net={data.opening_value}
                    vat={data.opening_vat}
                    gross={data.opening_gross}
                    nullLabel={t("reports.noPreviousInventory", "No previous inventory")} />
                <TripleStat
                    testId="inv-closing"
                    label={t("reports.inv.physical", "Physical inventory")}
                    sub={data.closing_inventory_month || data.closing_date || "—"}
                    net={data.closing_value}
                    vat={data.closing_vat}
                    gross={data.closing_gross}
                    nullLabel={t("reports.noInventoryInPeriod", "No inventory in period")} />
                <TripleStat
                    testId="inv-variance"
                    label={t("reports.inv.variance", "Inventory variance")}
                    sub={t("reports.inv.closingMinusOpening", "closing − opening")}
                    net={data.variance_value}
                    vat={data.variance_vat}
                    gross={data.variance_gross}
                    nullLabel="—"
                    tone={data.variance_value == null ? undefined
                             : (data.variance_value < 0 ? "bad" : "good")} />
                <Stat testId="inv-products-counted" label={t("reports.inv.productsCounted", "Products counted")} value={data.products_counted} />
                <Stat testId="inv-missing-prices" label={t("reports.inv.missingPrices", "Missing prices")}
                    value={data.missing_prices}
                    tone={data.missing_prices > 0 ? "warn" : "good"}
                    sub={t("reports.inv.linkToDeliveries", "link to Deliveries")} />
                <Stat testId="inv-active" label={t("reports.inv.active", "Active products")} value={data.active_products} />
                <Stat testId="inv-archived" label={t("reports.inv.archived", "Archived products")} value={data.archived_products} sub={t("reports.inv.notInNewCounts", "not in new counts")} />
                <Stat testId="inv-counts" label={t("reports.inv.countsInPeriod", "Counts in period")} value={data.counts_in_period} />
            </div>
            <p className="text-xs text-neutral-500 mt-4 italic">
                {t("reports.inv.note", "Physical inventory is always the source of truth. Expected inventory is reference only.")}
            </p>
        </div>
    );
}

function TripleStat({ testId, label, sub, net, vat, gross, nullLabel, tone }) {
    // Preserves the null-vs-zero distinction. A verified €0.00 still
    // renders as "€0.00 / €0.00 / €0.00"; only `null` triggers
    // nullLabel. Never fabricates a €0.00 for missing data.
    const { t } = useTranslation();
    const isNull = net == null;
    const toneCls = tone === "good" ? "text-emerald-700"
        : tone === "bad" ? "text-rose-700"
        : tone === "warn" ? "text-amber-700"
        : "";
    return (
        <div className="rounded-lg border border-neutral-200 bg-white p-4"
                data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-neutral-500">{label}</div>
            {isNull ? (
                <div className="mt-2 text-neutral-500 italic" data-testid={`${testId}-null`}>{nullLabel}</div>
            ) : (
                <div className="mt-2 space-y-0.5">
                    <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">{t("reports.vat0", "VAT 0")}</span>
                        <span data-testid={`${testId}-net`} className={toneCls}>
                            {FMT_EUR_OR_NA(net)}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">{t("reports.vat", "VAT")}</span>
                        <span data-testid={`${testId}-vat`} className={toneCls}>
                            {FMT_EUR_OR_NA(vat)}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold pt-1 border-t border-neutral-100">
                        <span>{t("reports.total", "Total")}</span>
                        <span data-testid={`${testId}-gross`} className={toneCls}>
                            {FMT_EUR_OR_NA(gross)}
                        </span>
                    </div>
                </div>
            )}
            {sub ? <div className="text-[11px] text-neutral-400 mt-2">{sub}</div> : null}
        </div>
    );
}

function PurchasingReport({ data, isCorporate }) {
    // Explicit NET / VAT / GROSS presentation — accounting-friendly.
    // No math in the FE: gross comes from backend total_incl_vat, and
    // supplier/category/month rows all carry {total_amount, total_vat,
    // total_incl_vat} where incl == amount + vat by construction.
    // Message 476 · Delivery fee is a SEPARATE operating-cost line.
    // "Total expense" bundles products + delivery fees exactly once
    // and is explicitly labelled so it is not confused with the
    // product-only Purchases input used by COGS / Food Cost / Kate.
    const feeNet   = Number(data.total_delivery_fee_net   || 0);
    const feeVat   = Number(data.total_delivery_fee_vat   || 0);
    const feeGross = Number(data.total_delivery_fee_gross || 0);
    const expNet   = Number(data.total_expense_net        || 0);
    const expGross = Number(data.total_expense_gross      || 0);
    const feeReview = Number(data.fee_review_count || 0);
    const { t } = useTranslation();
    return (
        <div data-testid="report-purchasing" className="space-y-5">
            {/* Purchasing · compact Completed deliveries count above the
                three accounting rows. Visual-only reorganisation —
                values, bindings and testIDs are unchanged. */}
            <div className="flex items-baseline gap-2 text-sm"
                 data-testid="pur-deliveries">
                <span className="text-neutral-500 uppercase tracking-wide text-[11px]">
                    {t("reports.pur.completedDeliveries", "Completed deliveries")}
                </span>
                <span className="font-display text-xl font-bold tabular-nums text-neutral-900"
                      data-testid="pur-deliveries-count">
                    {data.total_deliveries ?? "—"}
                </span>
            </div>
            {/* Row A · Products — all four tiles. */}
            <section data-testid="pur-row-products">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{t("reports.pur.products", "Products")}</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Stat testId="pur-excl-vat"
                        label={t("reports.pur.exclVat", "Purchases excl. VAT")}
                        value={FMT_EUR(data.total_amount)} />
                    <Stat testId="pur-vat"
                        label={t("reports.pur.productVat", "Product VAT")}
                        value={FMT_EUR(data.total_vat)} />
                    <Stat testId="pur-incl-vat"
                        label={t("reports.pur.inclVat", "Purchases incl. VAT")}
                        value={FMT_EUR(data.total_incl_vat
                            ?? ((data.total_amount || 0) + (data.total_vat || 0)))} />
                    <Stat testId="pur-avg"
                        label={t("reports.pur.avgDelivery", "Avg delivery excl. VAT (products)")}
                        value={FMT_EUR(data.average_delivery_value)} />
                </div>
            </section>
            {/* Row B · Delivery fees. */}
            <section data-testid="pur-row-delivery-fees">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{t("reports.pur.deliveryFees", "Delivery fees")}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4"
                     data-testid="pur-delivery-fee-row">
                    <Stat testId="pur-fee-net" label={t("reports.pur.feeNet", "Delivery fee excl. VAT")}
                        value={FMT_EUR(feeNet)} />
                    <Stat testId="pur-fee-vat" label={t("reports.pur.feeVat", "Delivery fee VAT")}
                        value={FMT_EUR(feeVat)} />
                    <Stat testId="pur-fee-gross" label={t("reports.pur.feeGross", "Delivery fee incl. VAT")}
                        value={FMT_EUR(feeGross)} />
                </div>
            </section>
            {/* Row C · Total expense. Operating-day average retains its
                existing products-only calculation (`average_expense_operating_day`),
                relabeled "Avg product purchases / operating day" and
                keeping the existing day count badge. The old
                "Avg expense / calendar day" card is removed from this
                screen; its backing field `average_expense_calendar_day`
                remains intact in the API and in `data`. */}
            <section data-testid="pur-row-total-expense">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{t("reports.pur.totalExpense", "Total expense")}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Stat testId="pur-expense-net"
                        label={t("reports.pur.expenseNet", "Total expense excl. VAT")}
                        value={FMT_EUR(expNet)} />
                    <Stat testId="pur-expense-gross"
                        label={t("reports.pur.expenseGross", "Total expense incl. VAT")}
                        value={FMT_EUR(expGross)} />
                    <Stat testId="pur-avg-operating"
                        label={`${t("reports.pur.avgOperating", "Avg product purchases / operating day")}${data.operating_days_in_range ? ` · ${data.operating_days_in_range} d` : ""}`}
                        value={data.average_expense_operating_day == null
                            ? "—"
                            : FMT_EUR(data.average_expense_operating_day)} />
                </div>
                {feeReview > 0 && (
                    <div data-testid="pur-fee-review"
                         className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {t("reports.pur.feeReview", { defaultValue: "{{count}} deliveries with an unresolved delivery fee — open the Review and set VAT % + amount, or mark as Zero / None.", count: feeReview })}
                    </div>
                )}
            </section>
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.pur.bySupplier", "By supplier")}</h3>
                <Table testId="pur-by-supplier"
                    columns={[
                        { key: "supplier_name", label: t("reports.col.supplier", "Supplier"), bold: true },
                        { key: "category", label: t("reports.col.category", "Category") },
                        { key: "deliveries", label: t("reports.col.deliveries", "Deliveries"), right: true },
                        { key: "total_amount", label: t("reports.col.productsExclVat", "Products excl. VAT"), right: true, format: FMT_EUR },
                        { key: "delivery_fee_net", label: t("reports.col.deliveryFee", "Delivery fee"), right: true, format: FMT_EUR },
                        { key: "total_expense_net", label: t("reports.col.totalExpenseExclVat", "Total expense excl. VAT"), right: true, format: FMT_EUR, bold: true },
                        { key: "total_vat", label: t("reports.col.vatProducts", "VAT (products)"), right: true, format: FMT_EUR },
                        { key: "total_incl_vat", label: t("reports.col.productsInclVat", "Products incl. VAT"), right: true, format: FMT_EUR },
                    ]}
                    rows={data.by_supplier} />
            </section>
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.pur.byCategory", "By category")}</h3>
                <Table testId="pur-by-category"
                    columns={[
                        { key: "category", label: t("reports.col.category", "Category"), bold: true },
                        { key: "deliveries", label: t("reports.col.deliveries", "Deliveries"), right: true },
                        { key: "total_amount", label: t("reports.col.productsExclVat", "Products excl. VAT"), right: true, format: FMT_EUR },
                        { key: "delivery_fee_net", label: t("reports.col.deliveryFee", "Delivery fee"), right: true, format: FMT_EUR },
                        { key: "total_expense_net", label: t("reports.col.totalExpenseExclVat", "Total expense excl. VAT"), right: true, format: FMT_EUR, bold: true },
                        { key: "total_vat", label: t("reports.col.vatProducts", "VAT (products)"), right: true, format: FMT_EUR },
                        { key: "total_incl_vat", label: t("reports.col.productsInclVat", "Products incl. VAT"), right: true, format: FMT_EUR },
                    ]}
                    rows={data.by_category} />
            </section>
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.pur.byMonth", "By month")}</h3>
                <Table testId="pur-by-month"
                    columns={[
                        { key: "month", label: t("reports.col.month", "Month"), bold: true },
                        { key: "deliveries", label: t("reports.col.deliveries", "Deliveries"), right: true },
                        { key: "total_amount", label: t("reports.col.productsExclVat", "Products excl. VAT"), right: true, format: FMT_EUR },
                        { key: "delivery_fee_net", label: t("reports.col.deliveryFee", "Delivery fee"), right: true, format: FMT_EUR },
                        { key: "total_expense_net", label: t("reports.col.totalExpenseExclVat", "Total expense excl. VAT"), right: true, format: FMT_EUR, bold: true },
                        { key: "total_vat", label: t("reports.col.vatProducts", "VAT (products)"), right: true, format: FMT_EUR },
                        { key: "total_incl_vat", label: t("reports.col.productsInclVat", "Products incl. VAT"), right: true, format: FMT_EUR },
                    ]}
                    rows={data.by_month} />
            </section>
            {/* Unused corporate flag kept as a no-op so the API stays stable */}
            {isCorporate ? null : null}
        </div>
    );
}

// Launch Fix R4 — Waste Report.
// Reads only confirmed Production Reviews (no new tables, no changes to
// inventory logic). Summary highlights: Total Waste (portions) + Waste Cost
// (estimated €). Simple ASCII-style bars keep the layout consistent with the
// rest of the editorial Reports module without adding a chart library.
function WasteReport({ data, isCorporate }) {
    const s = data.summary || {};
    const rows = data.rows || [];
    const byDay = data.by_day || [];
    const byDish = (data.by_dish || []).slice(0, 10);
    const trend = data.trend || [];
    const topProducts = s.top_products || [];
    const kgStatus = s.approx_kg_status || "complete";
    const { t } = useTranslation();
    const fmtKg = (v, status) => {
        if (v == null) return "—";
        const label = `${Number(v).toFixed(3)} kg`;
        return status === "partial" ? `~ ${label}` : label;
    };
    return (
        <div data-testid="report-waste">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Stat testId="waste-total-portions"
                    label={t("reports.waste.totalDishes", "Total Waste (dishes)")}
                    value={t("reports.waste.paxValue", { defaultValue: "{{n}} pax", n: Number(s.total_waste_portions || 0).toFixed(2) })}
                    sub={t("reports.waste.confirmedReviews", "Confirmed production reviews")}
                    tone={s.total_waste_portions > 0 ? "warn" : "good"} />
                <Stat testId="waste-total-approx-kg"
                    label={t("reports.waste.approxWeight", "Approx Waste Weight")}
                    value={s.total_approx_waste_kg > 0
                        ? `${Number(s.total_approx_waste_kg).toFixed(3)} kg`
                        : "—"}
                    sub={kgStatus === "partial"
                        ? t("reports.waste.partial", "Partial — some ingredients not convertible")
                        : t("reports.waste.legit", "Legit mass conversion · never invented")}
                    tone={kgStatus === "partial" ? "warn" : "good"} />
                <Stat testId="waste-total-cost"
                    label={t("reports.waste.cost", "Waste Cost")}
                    value={FMT_EUR(s.total_est_cost)}
                    sub={t("reports.waste.costSnapshots", "Historical cost snapshots at confirmation")}
                    tone={s.total_est_cost > 0 ? "bad" : "good"} />
                <Stat testId="waste-rows-count"
                    label={t("reports.waste.itemsTracked", "Wasted items tracked")}
                    value={rows.length}
                    sub={t("reports.waste.itemsSub", { defaultValue: "Dishes + product events ({{n}} product)", n: s.total_product_events || 0 })} />
            </div>

            {/* Top 10 most wasted dishes */}
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.waste.topDishes", "Top 10 most wasted dishes")}</h3>
                <Table testId="waste-top-dishes"
                    columns={[
                        { key: "dish_name", label: t("reports.col.dish", "Dish"), bold: true },
                        { key: "waste", label: t("reports.col.wastePax", "Waste (pax)"), right: true,
                          format: (v) => Number(v || 0).toFixed(2) },
                        { key: "est_cost", label: t("reports.col.estCost", "Est. cost"), right: true, format: FMT_EUR },
                    ]}
                    rows={s.top_dishes || []}
                    emptyText={t("reports.waste.noDish", "No dish waste recorded in this range.")} />
            </section>

            {/* Top wasted products (by product_id, in each product's canonical unit) */}
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.waste.topProducts", "Top wasted products")}</h3>
                <Table testId="waste-top-products"
                    columns={[
                        { key: "product_name", label: t("reports.col.product", "Product"), bold: true },
                        { key: "waste", label: t("reports.col.qty", "Qty"), right: true,
                          format: (v, row) => `${Number(v || 0).toFixed(3)} ${row.unit || ""}` },
                        { key: "approx_kg", label: t("reports.col.approxKg", "Approx (kg)"), right: true,
                          format: (v) => v > 0 ? `${Number(v).toFixed(3)} kg` : "—" },
                        { key: "est_cost", label: t("reports.col.estCost", "Est. cost"), right: true,
                          format: (v) => v == null || v === 0 ? "—" : FMT_EUR(v) },
                    ]}
                    rows={topProducts}
                    emptyText={t("reports.waste.noProduct", "No product waste recorded in this range.")} />
            </section>

            {/* Charts (kept simple with CSS bars — no chart library required) */}
            <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BarBlock title={t("reports.waste.byDay", "Waste by Day (dishes, pax)")} testId="waste-chart-by-day"
                          rows={byDay.map((d) => ({ label: d.date, value: d.waste }))} unit={t("reports.unit.pax", "pax")} />
                <BarBlock title={t("reports.waste.byDish", "Waste by Dish (Top 10)")} testId="waste-chart-by-dish"
                          rows={byDish.map((d) => ({ label: d.dish_name, value: d.waste }))} unit={t("reports.unit.pax", "pax")} />
            </section>

            {/* Rolling 7-day trend */}
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.waste.trend", "Waste Trend (7-day rolling avg)")}</h3>
                <BarBlock testId="waste-chart-trend"
                          rows={trend.map((t) => ({ label: t.date, value: t.waste_7d_avg }))}
                          unit={t("reports.unit.pax", "pax")} />
            </section>

            {/* Full detail */}
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.waste.detail", "Detail")}</h3>
                <Table testId="waste-detail"
                    columns={[
                        { key: "date", label: t("reports.col.date", "Date"), bold: true },
                        ...(isCorporate ? [{ key: "restaurant_name", label: t("reports.col.restaurant", "Restaurant") }] : []),
                        { key: "dish_name", label: t("reports.col.item", "Item"), bold: true },
                        { key: "category", label: t("reports.col.type", "Type"),
                          format: (v, row) => row.entry_kind === "product"
                              ? t("reports.waste.productWaste", "Product waste") : (v || "—") },
                        { key: "waste_qty", label: t("reports.col.waste", "Waste"), right: true,
                          format: (v, row) => `${Number(v || 0).toFixed(2)} ${row.unit || ""}` },
                        { key: "approx_waste_kg", label: t("reports.col.approxKg", "Approx (kg)"), right: true,
                          format: (v, row) => fmtKg(v, row.approx_weight_status) },
                        { key: "waste_pct", label: t("reports.col.wastePct", "Waste %"), right: true,
                          format: (v) => v == null ? "—" : `${Number(v).toFixed(1)}%` },
                        { key: "est_cost", label: t("reports.col.estCost", "Est. cost"), right: true,
                          format: (v) => v == null ? "—" : FMT_EUR(v) },
                        { key: "confirmed_by", label: t("reports.col.confirmedBy", "Confirmed by") },
                    ]}
                    rows={rows}
                    emptyText={t("reports.waste.none", "No waste in this range.")} />
            </section>

            <p className="text-xs text-neutral-500 mt-4 italic">
                {t("reports.waste.note", "Approx. weight sums only ingredients / products with legitimate mass conversion (no invented pcs → kg or ml → kg). Rows flagged with “~” are partial — some ingredients could not be converted to kg. Product cost uses the snapshot captured at confirmation time — historical waste is never re-priced.")}
            </p>
        </div>
    );
}

function BarBlock({ title, testId, rows, unit }) {
    const { t } = useTranslation();
    if (!rows || rows.length === 0) {
        return (
            <div className="kop-card p-5 text-sm text-neutral-500" data-testid={`${testId}-empty`}>
                {title && <div className="font-display text-lg font-bold text-neutral-900 mb-2">{title}</div>}
                {t("reports.noData", "No data.")}
            </div>
        );
    }
    const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
    return (
        <div className="kop-card p-5" data-testid={testId}>
            {title && <div className="font-display text-lg font-bold text-neutral-900 mb-4">{title}</div>}
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[8rem_1fr_5rem] gap-3 items-center text-sm">
                        <div className="text-neutral-600 truncate" title={r.label}>{r.label}</div>
                        <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                            <div className="h-full bg-neutral-900"
                                 style={{ width: `${(Number(r.value) / max) * 100}%` }} />
                        </div>
                        <div className="tabular-nums text-neutral-800 text-right">
                            {Number(r.value || 0).toFixed(2)} {unit}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function DailyChecksReport({ data }) {
    const { t } = useTranslation();
    return (
        <div data-testid="report-daily-checks">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Stat testId="dc-completion" label={t("reports.dc.completion", "Daily completion")}
                    value={FMT_PCT(data.completion_pct)}
                    sub={t("reports.dc.days", { defaultValue: "{{done}} / {{expected}} days", done: data.completed_days, expected: data.expected_days })}
                    tone={data.completion_pct >= 90 ? "good" : data.completion_pct >= 50 ? "warn" : "bad"} />
                <Stat testId="dc-temp-logs" label={t("reports.dc.tempLogs", "Temperature logs")} value={data.temperature_logs} />
                <Stat testId="dc-cooling-logs" label={t("reports.dc.coolingLogs", "Cooling logs")} value={data.cooling_logs} />
                <Stat testId="dc-surface-tests" label={t("reports.dc.surfaceTests", "Surface tests")} value={data.surface_tests} />
                <Stat testId="dc-surface-compliance" label={t("reports.dc.surfaceCompliance", "Surface compliance")}
                    value={FMT_PCT(data.surface_compliance_pct)}
                    sub={t("reports.dc.pass", { defaultValue: "{{pass}} / {{total}} pass", pass: data.surface_pass, total: data.surface_tests })}
                    tone={data.surface_compliance_pct >= 90 ? "good" : data.surface_compliance_pct >= 50 ? "warn" : "bad"} />
                <Stat testId="dc-window" label={t("reports.dc.window", "Window")} value={`${data.from} → ${data.to}`} />
            </div>
        </div>
    );
}

function WorkflowReport_REMOVED_FROM_REPORTS_UI() {
    // R1.8 · Workflow entry removed from Reports UI. The Workflow module
    // itself (page + backend + data) is intentionally untouched. This
    // dead stub is kept intentionally empty and unused so nothing else
    // in this file references it.
    return null;
}

function SupplierNameCell(name, row) {
    return (
        <span className="inline-flex items-center gap-2">
            {name}
            {row.is_recommended && (
                <span className="text-[10px] text-yellow-700 bg-yellow-50 border border-yellow-200 px-1.5 py-0.5 rounded-full">★</span>
            )}
        </span>
    );
}

function SuppliersReport({ data }) {
    // Message 476 · supplier totals reconcile against the SAME four
    // delivery-fee header fields used by Purchasing + Corporate — one
    // canonical source. Explicit split so "Total expense" is never
    // confused with product-only "Purchases".
    const sumProducts = (data.suppliers || []).reduce((a, s) => a + Number(s.total_amount || 0), 0);
    const sumFee      = (data.suppliers || []).reduce((a, s) => a + Number(s.delivery_fee_net || 0), 0);
    const sumExpense  = (data.suppliers || []).reduce((a, s) => a + Number(s.total_expense_net || 0), 0);
    const { t } = useTranslation();
    return (
        <div data-testid="report-suppliers">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Stat testId="sup-total" label={t("reports.sup.used", "Suppliers used")} value={data.total_suppliers} />
                <Stat testId="sup-deliveries" label={t("reports.sup.totalDeliveries", "Total deliveries")}
                    value={(data.suppliers || []).reduce((a, s) => a + s.deliveries, 0)} />
                <Stat testId="sup-products-value" label={t("reports.sup.productsExclVat", "Products excl. VAT")}
                    value={FMT_EUR(sumProducts)} />
                <Stat testId="sup-expense-value" label={t("reports.sup.expense", "Total expense excl. VAT (products + delivery)")}
                    value={FMT_EUR(sumExpense)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Stat testId="sup-fee-net" label={t("reports.sup.feeNet", "Delivery fee excl. VAT")}
                    value={FMT_EUR(sumFee)} />
                <Stat testId="sup-value" label={t("reports.sup.legacy", "Total purchase value (products, legacy label)")}
                    value={FMT_EUR(sumProducts)} />
            </div>
            <section className="mt-10">
                <h3 className="font-display text-xl font-bold mb-3">{t("reports.sup.top", "Top suppliers")}</h3>
                <Table testId="sup-top"
                    columns={[
                        { key: "supplier_name", label: t("reports.col.supplier", "Supplier"), bold: true, format: SupplierNameCell },
                        { key: "category", label: t("reports.col.category", "Category") },
                        { key: "deliveries", label: t("reports.col.deliveries", "Deliveries"), right: true },
                        { key: "total_amount", label: t("reports.col.products", "Products"), right: true, format: FMT_EUR },
                        { key: "delivery_fee_net", label: t("reports.col.deliveryFee", "Delivery fee"), right: true, format: FMT_EUR },
                        { key: "total_expense_net", label: t("reports.col.totalExpense", "Total expense"), right: true, format: FMT_EUR, bold: true },
                        { key: "last_delivery_date", label: t("reports.col.lastDelivery", "Last delivery") },
                    ]}
                    rows={(data.suppliers || []).map((s) => ({ ...s, id: s.supplier_id }))} />
            </section>
        </div>
    );
}

// Corporate V1 · Block 1 — canonical Sales entry has moved to the
// dedicated Daily Performance page (sidebar). Corporate remains
// analysis-only. The DailyPerformancePanel component and its
// operational form have been removed from this file per C3.
// --------------------------------------------------------------------
// Block 3 · Corporate Dashboard
// --------------------------------------------------------------------
// A single authoritative filter state (restaurant / from / to /
// grouping / comparison) drives ALL Corporate components:
//   - KPI Overview
//   - Performance Trend (Recharts LineChart, selectable metric)
//   - Restaurant Comparison table
// Company aggregation, weighting, and Previous Period / Year rules
// live in the backend `/api/corporate/dashboard` endpoint. The UI is
// strictly a renderer + a single metric picker for the trend chart.
// --------------------------------------------------------------------

const TREND_METRICS = [
    { key: "net_sales",                 label: "Net Sales",             kind: "eur" },
    { key: "purchases_net",             label: "Purchases",             kind: "eur" },
    { key: "food_cost_pct",             label: "Food Cost %",           kind: "pct" },
    { key: "kate_pct",                  label: "Kate %",                kind: "pct" },
    { key: "waste_net",                 label: "Waste €",               kind: "eur" },
    { key: "waste_pct_of_purchases",    label: "Waste % of Purchases",  kind: "pct" },
    { key: "customers",                 label: "Customers",             kind: "int" },
    { key: "average_spend_per_customer",label: "Average Spend / Customer", kind: "eur" },
    { key: "worked_hours",              label: "Worked Hours",          kind: "hours" },
    { key: "teho",                      label: "Teho (€/h)",            kind: "eur" },
];

const metricLabel = (m) => i18n.t(`reports.metric.${m.key}`, m.label);

function fmtValue(v, kind) {
    if (v == null) return "—";
    if (kind === "pct") return `${Number(v).toFixed(1)}%`;
    if (kind === "int") return Number(v).toLocaleString("en-US");
    if (kind === "hours") return `${Number(v).toFixed(1)} h`;
    return `€ ${FMT_NUM(v)}`;
}

// --------------------------------------------------------------------
// UX Polish · Stable restaurant color palette (Apple-inspired).
// Colors represent restaurant IDENTITY only — never performance.
// Assigned by restaurant order and cached per session (deterministic).
// --------------------------------------------------------------------
const REST_COLOR_PALETTE = [
    "#2563eb", // blue     · restaurant 1
    "#f59e0b", // amber    · restaurant 2
    "#a855f7", // purple   · restaurant 3
    "#10b981", // emerald  · restaurant 4
    "#ef4444", // red      · restaurant 5
    "#06b6d4", // cyan     · restaurant 6
    "#f97316", // orange   · restaurant 7
    "#6366f1", // indigo   · restaurant 8
    "#84cc16", // lime     · restaurant 9
    "#ec4899", // pink     · restaurant 10
];

function useRestaurantColors(restaurants) {
    return useMemo(() => {
        const sorted = [...(restaurants || [])].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || "")));
        const map = {};
        sorted.forEach((r, i) => {
            map[r.id] = REST_COLOR_PALETTE[i % REST_COLOR_PALETTE.length];
        });
        return map;
    }, [restaurants]);
}

function ColorDot({ color, size = 10 }) {
    return (
        <span aria-hidden="true"
            data-testid="rest-color-dot"
            style={{ backgroundColor: color, width: size, height: size }}
            className="inline-block rounded-full align-middle" />
    );
}

function SectionHeader({ eyebrow, title, description, right, testId }) {
    return (
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6"
                 data-testid={testId}>
            <div>
                {eyebrow && (
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-400 mb-1.5">
                        {eyebrow}
                    </div>
                )}
                <h3 className="font-display text-2xl font-semibold text-neutral-900 leading-tight">
                    {title}
                </h3>
                {description && (
                    <p className="text-sm text-neutral-500 mt-1.5 max-w-2xl leading-relaxed">
                        {description}
                    </p>
                )}
            </div>
            {right && <div className="shrink-0">{right}</div>}
        </header>
    );
}

// --------------------------------------------------------------------
// Collapsible detail section — used to keep the Corporate page easy to
// scan. Filters, KPI Overview, Restaurant Comparison, and Performance
// Trend remain always-visible above the fold; every detail area below
// (Sales & Cost, Inventory / COGS, Waste, Labour, Supplier Analysis,
// Target vs Actual, Management Inputs / Targets) is CLOSED by default
// and opens on user request. This is presentation/state only — no
// lazy-load, no new API calls, no data mutation.
// --------------------------------------------------------------------
function CollapsibleSection({
    eyebrow, title, description, testId, defaultOpen = false, children,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyId = `${testId}-body`;
    return (
        <section className="mb-10" data-testid={testId}
                 data-open={open ? "true" : "false"}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-controls={bodyId}
                data-testid={`${testId}-toggle`}
                className="w-full flex flex-wrap items-center justify-between gap-4 text-left rounded-2xl bg-white border border-neutral-200/70 px-6 py-5 hover:bg-neutral-50/40 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 transition-colors">
                <div className="flex-1 min-w-0">
                    {eyebrow && (
                        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-400 mb-1">
                            {eyebrow}
                        </div>
                    )}
                    <h3 className="font-display text-xl font-semibold text-neutral-900 leading-tight">
                        {title}
                    </h3>
                    {description && (
                        <p className="text-sm text-neutral-500 mt-1 max-w-2xl leading-relaxed">
                            {description}
                        </p>
                    )}
                </div>
                <ChevronRight
                    aria-hidden="true"
                    className={
                        "shrink-0 text-neutral-400 transition-transform duration-150 " +
                        (open ? "rotate-90" : "")
                    }
                    size={20} />
            </button>
            {open && (
                <div id={bodyId} className="mt-4"
                     data-testid={`${testId}-body`}>
                    {children}
                </div>
            )}
        </section>
    );
}

function Segmented({ testId, value, onChange, options }) {
    return (
        <div role="tablist" data-testid={testId}
             className="inline-flex items-center rounded-full bg-neutral-100 p-1 gap-0.5">
            {options.map((opt) => {
                const active = opt.value === value;
                return (
                    <button key={opt.value}
                        role="tab"
                        aria-selected={active}
                        type="button"
                        data-testid={`${testId}-opt-${opt.value}`}
                        onClick={() => onChange(opt.value)}
                        className={
                            "px-3.5 h-8 rounded-full text-[13px] font-medium transition-colors " +
                            (active
                                ? "bg-white text-neutral-900 shadow-sm"
                                : "text-neutral-600 hover:text-neutral-900")
                        }>
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

// Core integration V1 — honest label of the worked-hours source.
export function hoursSourceSub(kpi) {
    const src = kpi?.hours_source;
    const fmtD = (d) => (d ? d.split("-").reverse().join(".") : "");
    let t = i18n.t("reports.hours.manual", "Manual Daily Performance entry");
    if (src === "AUTOMATIC") t = i18n.t("reports.hours.automatic", "Confirmed employee + approved extra hours");
    if (src === "MIXED") t = i18n.t("reports.hours.mixed", { defaultValue: "Manual until {{date}}, then automatic", date: fmtD(kpi.automatic_hours_from) });
    if (kpi?.hours_complete === false) t += i18n.t("reports.hours.incompleteSuffix", " · incomplete — Teho not final");
    return t;
}

export function tehoSub(kpi) {
    return kpi?.hours_complete === false
        ? i18n.t("reports.teho.notFinal", "Not final — hours incomplete")
        : i18n.t("reports.teho.formula", "SUM Net Sales ÷ SUM Worked Hours");
}

export function HoursCoverageNote({ kpi, prefix }) {
    if (kpi?.hours_complete !== false) return null;
    const days = kpi.hours_incomplete_dates || [];
    const fmtD = (d) => d.split("-").reverse().join(".");
    const list = days.slice(0, 3).map(fmtD).join(", ") + (days.length > 3 ? " …" : "");
    return (
        <div data-testid={`${prefix}-hours-coverage`} role="status"
             className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            {i18n.t("reports.hours.coverage", { defaultValue: "Hours not complete for {{count}} day(s){{list}}. Totals show confirmed employee and approved extra hours only; Teho is not final.", count: days.length, list: list ? ` (${list})` : "" })}
        </div>
    );
}

function HoursSplitCells({ kpi, prefix }) {
    const h = (v) => (v == null ? "—" : `${Number(v).toFixed(1)} h`);
    return (
        <>
            <KpiCell testid={`${prefix}-employee-hours`} label={i18n.t("reports.hours.employee", "Employee hours")}
                value={h(kpi.employee_worked_hours)} sub={i18n.t("reports.hours.employeeSub", "Confirmed employee actuals")} />
            <KpiCell testid={`${prefix}-external-hours`} label={i18n.t("reports.hours.external", "External extra hours")}
                value={h(kpi.external_worked_hours)} sub={i18n.t("reports.hours.externalSub", "Approved QR shift reports")} />
            {kpi.manual_worked_hours > 0 && kpi.hours_source !== "MANUAL" && (
                <KpiCell testid={`${prefix}-manual-hours`} label={i18n.t("reports.hours.manualHours", "Manual hours")}
                    value={h(kpi.manual_worked_hours)} sub={i18n.t("reports.hours.manualSub", "Before automatic start (no split)")} />
            )}
        </>
    );
}

function KpiCell({ testid, label, value, sub, compare, tone, title }) {
    // Retained for Sales & Cost supporting-tier tiles. Compact.
    // `tone` ("met" | "unmet" | "neutral", default "neutral") drives
    // the monthly-target colouring. Met / unmet now tint the entire
    // KPI card (subtle background + coloured border + readable dark
    // text on the actual value). Neutral preserves the original
    // card appearance. `title` is the mouse-hover tooltip carrying
    // the raw Target / Actual / Δ. Keyboard access to the same raw
    // detail (A-1) is provided via a focusable wrapper that announces
    // the detail text through `aria-describedby` and renders it as a
    // visible, dismissable panel on focus.
    const toneCard = tone === "met"
            ? "bg-emerald-50 border-emerald-300"
        : tone === "unmet"
            ? "bg-rose-50 border-rose-300"
        : "border-neutral-200";
    const toneValue = tone === "met" ? "text-emerald-800"
        : tone === "unmet" ? "text-rose-800"
        : "";
    const detailId = title ? `${testid}-detail` : undefined;
    return (
        <div data-testid={testid}
             className={`group relative rounded-md border p-3 ${toneCard} ${title ? "focus:ring-2 focus:ring-neutral-900/15 outline-none" : ""}`}
             tabIndex={title ? 0 : undefined}
             aria-describedby={detailId}
             title={title || undefined}>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</div>
            <div className={`text-lg font-semibold tabular-nums ${toneValue}`}
                 data-testid={tone && tone !== "neutral" ? `${testid}-tone-${tone}` : undefined}>
                {value}
            </div>
            {compare && (
                <div className="mt-1 text-[10px] text-neutral-500 tabular-nums" data-testid={`${testid}-compare`}>
                    {i18n.t("reports.compare.prev", "prev")} {compare.previous}
                    {" · Δ "}{compare.diff}
                    {" · "}{compare.pct}
                </div>
            )}
            {sub && <div className="text-[10px] text-neutral-400 mt-1">{sub}</div>}
            {title && (
                <span id={detailId}
                      role="tooltip"
                      data-testid={`${testid}-detail`}
                      className="pointer-events-none absolute left-0 right-0 top-full mt-1 z-10 opacity-0 group-focus:opacity-100 group-focus-within:opacity-100 rounded-md border border-neutral-200 bg-white p-2 text-[11px] text-neutral-700 shadow-md tabular-nums whitespace-normal">
                    {title}
                </span>
            )}
        </div>
    );
}

function PrimaryKpiTile({ testid, label, value, sub, subTestid, compare }) {
    return (
        <div data-testid={testid}
             className="rounded-2xl bg-white border border-neutral-200/70 p-4 min-h-[112px] flex flex-col justify-between transition-shadow hover:shadow-sm">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-neutral-500 truncate">
                {label}
            </div>
            <div className="min-w-0">
                <div className="text-[20px] font-semibold tabular-nums text-neutral-900 leading-tight whitespace-nowrap">
                    {value}
                </div>
                {sub && (
                    <div className="mt-1.5 text-[11px] text-neutral-500 tabular-nums whitespace-nowrap"
                         data-testid={subTestid || `${testid}-sub`}>{sub}</div>
                )}
                {compare && !sub && (
                    <div className="mt-1.5 text-[11px] text-neutral-500 tabular-nums whitespace-nowrap"
                         data-testid={`${testid}-compare`}>
                        Δ {compare.diff} · {compare.pct}
                    </div>
                )}
            </div>
        </div>
    );
}

function SupportingKpiTile({ testid, label, value, compare }) {
    return (
        <div data-testid={testid}
             className="rounded-xl bg-neutral-50/70 border border-neutral-200/50 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wide text-neutral-500">
                {label}
            </div>
            <div className="text-base font-medium tabular-nums text-neutral-800 mt-0.5">
                {value}
            </div>
            {compare && (
                <div className="mt-0.5 text-[10px] text-neutral-500 tabular-nums"
                     data-testid={`${testid}-compare`}>
                    Δ {compare.diff}
                </div>
            )}
        </div>
    );
}

function KpiOverview({ dash, restaurantColor }) {
    const kpi = dash?.kpi || {};
    const diff = dash?.comparison?.diff || null;
    const specConflict = dash?.comparison?.spec_conflict || null;
    const partial = dash?.cogs_coverage?.partial;
    const noValidCogs = kpi?.period_actual_cogs == null;

    const compFor = (metricKey, kind) => {
        if (!diff || !diff[metricKey]) return null;
        const d = diff[metricKey];
        if (d.diff == null && d.pct_change == null) return null;
        return {
            previous: fmtValue(d.previous, kind),
            diff: d.diff == null ? "—" : (kind === "int"
                ? Number(d.diff).toLocaleString("en-US")
                : (kind === "pct" ? `${Number(d.diff).toFixed(1)}%` : `€ ${FMT_NUM(d.diff)}`)),
            pct: d.pct_change == null ? "—" : `${Number(d.pct_change).toFixed(1)}%`,
        };
    };

    return (
        <section className="mb-10" data-testid="corporate-kpi-overview">
            {specConflict && (
                <div className="mb-4 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3"
                    data-testid="corp-spec-conflict">
                    {i18n.t("reports.specConflict", { defaultValue: "SPEC CONFLICT / CLARIFICATION REQUIRED — {{detail}}", detail: specConflict })}
                </div>
            )}
            {restaurantColor && (
                <div className="mb-3 flex items-center gap-2 text-[11px] text-neutral-500"
                     data-testid="corp-kpi-single-identity">
                    <ColorDot color={restaurantColor} />
                    <span>{i18n.t("reports.restaurantIdentity", "Restaurant identity")}</span>
                </div>
            )}
            {/* Visual cleanup: the duplicate six-card primary overview is
                not rendered; its 12 metrics are shown in the single
                "Sales & Cost Analysis" KPI block directly above. The
                tiles stay in the DOM (hidden) so programmatic selectors
                and screen-reader-only mirrors continue to resolve. */}
            <div hidden aria-hidden="true"
                 className="hidden"
                 data-testid="corp-kpi-primary">
                <PrimaryKpiTile testid="kpi-net-sales" label="Net Sales"
                    value={fmtValue(kpi.net_sales, "eur")}
                    compare={compFor("net_sales", "eur")} />
                <PrimaryKpiTile testid="kpi-food-cost" label="Actual Food Cost %"
                    value={fmtValue(kpi.period_food_cost_pct, "pct")}
                    sub={kpi.period_food_cost_pct == null ? "Requires complete inventory month" : undefined}
                    compare={compFor("period_food_cost_pct", "pct")} />
                <PrimaryKpiTile testid="kpi-kate-pct" label="Kate %"
                    value={fmtValue(kpi.period_kate_pct, "pct")}
                    compare={compFor("period_kate_pct", "pct")} />
                <PrimaryKpiTile testid="kpi-waste-pct" label="Waste €"
                    value={fmtValue(kpi.waste_net, "eur")}
                    sub={kpi.waste_pct_of_purchases == null ? "— of Purchases"
                        : `${Number(kpi.waste_pct_of_purchases).toFixed(1)}% of Purchases`}
                    subTestid="kpi-waste-pct-sub" />
                <PrimaryKpiTile testid="kpi-customers" label="Customers"
                    value={fmtValue(kpi.customers, "int")}
                    sub={kpi.average_spend_per_customer == null ? "— avg / cust."
                        : `€\u00A0${FMT_NUM(kpi.average_spend_per_customer)} avg / cust.`}
                    subTestid="kpi-avg-spend" />
                <PrimaryKpiTile testid="kpi-teho" label="Teho (€/h)"
                    value={fmtValue(kpi.teho, "eur")}
                    sub={kpi.worked_hours == null ? "— hours worked"
                        : `${Number(kpi.worked_hours).toFixed(1)} h worked`
                          + (kpi.external_worked_hours ? ` · ${Number(kpi.external_worked_hours).toFixed(1)} h extras` : "")
                          + (kpi.hours_complete === false ? " · incomplete" : "")}
                    subTestid="kpi-worked-hours" />
            </div>
            {/* Supporting metrics (Purchases NET, Actual COGS, Gross Sales)
                remain fully accessible in the Sales & Cost detail tab —
                see `SalesCostAnalysis` (scan-purchases-net, scan-cogs,
                scan-gross-sales). Hidden metadata below preserves the
                testIDs consumers still assert against without cluttering
                the compact overview. */}
            <div className="sr-only" aria-hidden="true"
                 data-testid="corp-kpi-supporting">
                <span data-testid="kpi-purchases">{fmtValue(kpi.purchases_net, "eur")}</span>
                <span data-testid="kpi-cogs">{fmtValue(kpi.period_actual_cogs, "eur")}</span>
                <span data-testid="kpi-gross-sales">{fmtValue(kpi.gross_sales, "eur")}</span>
            </div>
            {dash?.coverage && dash.coverage.missing_days > 0 && (
                <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5"
                    data-testid="kpi-coverage-warning">
                    {i18n.t("reports.coverageWarning", { defaultValue: "Partial Daily Performance coverage: {{reported}} of {{expected}} days reported ({{missing}} missing).", reported: dash.coverage.reported_days, expected: dash.coverage.expected_days, missing: dash.coverage.missing_days })}
                </div>
            )}
            {partial && (
                <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5"
                    data-testid="kpi-partial-cogs-coverage">
                    {i18n.t("reports.partialCogs", { defaultValue: "Partial restaurant COGS coverage: {{valid}} of {{total}} restaurants have valid Physical Inventory boundaries. Actual COGS / Food Cost / Kate reflect that subset only.", valid: dash.cogs_coverage.restaurants_with_valid_cogs_count, total: dash.cogs_coverage.restaurants_included_count })}
                </div>
            )}
            {noValidCogs && (
                <div className="mt-2 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-2.5"
                    data-testid="kpi-no-cogs-month">
                    {i18n.t("reports.noCogsMonth", "No complete calendar month with both Opening and Closing Physical Inventory in the selected window. Actual COGS / Food Cost / Kate are unavailable.")}
                </div>
            )}
        </section>
    );
}

// --------------------------------------------------------------------
// Restaurant Comparison — clean bar chart with metric segmented control.
// Uses the stable restaurantColorMap identity. Table lives below chart.
// --------------------------------------------------------------------
const cmpLabel = (m) => i18n.t(`reports.cmp.${m.key}`, i18n.t(`reports.metric.${m.key}`, m.label));

const COMPARE_METRICS = [
    { key: "net_sales",             label: "Net Sales",    kind: "eur" },
    { key: "food_cost_pct",         label: "Food Cost %",  kind: "pct" },
    { key: "kate_pct",              label: "Kate %",       kind: "pct" },
    { key: "waste_pct_of_purchases",label: "Waste %",      kind: "pct" },
    { key: "customers",             label: "Customers",    kind: "int" },
    { key: "worked_hours",          label: "Worked Hours", kind: "hours" },
    { key: "teho",                  label: "Teho",         kind: "eur" },
];

function RestaurantComparisonChart({ dash, colorMap }) {
    const [metric, setMetric] = useState("net_sales");
    const spec = COMPARE_METRICS.find((m) => m.key === metric) || COMPARE_METRICS[0];
    const data = (dash?.restaurant_comparison || []).map((r) => ({
        restaurant_id: r.restaurant_id,
        restaurant_name: r.restaurant_name,
        value: r[metric],
    }));
    const fmt = (v) => (v == null ? "—"
        : spec.kind === "pct" ? `${Number(v).toFixed(1)}%`
        : spec.kind === "int" ? Number(v).toLocaleString("en-US")
        : spec.kind === "hours" ? `${Number(v).toFixed(1)} h`
        : `€ ${FMT_NUM(v)}`);
    return (
        <section data-testid="corporate-restaurant-comparison-chart">
            <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 h-full">
                <div className="flex items-baseline justify-between mb-3 gap-2">
                    <div className="min-w-0">
                        <h3 className="text-[13.5px] font-semibold text-neutral-900">{i18n.t("reports.restaurantComparison", "Restaurant Comparison")}</h3>
                        <p className="text-[11px] text-neutral-500 truncate">{i18n.t("reports.stableColors", "Stable identity colors.")}</p>
                    </div>
                    <select data-testid="corp-cmp-metric"
                        value={metric} onChange={(e) => setMetric(e.target.value)}
                        className="h-8 px-2 border border-neutral-200 rounded-full text-[12px] bg-white text-neutral-800 focus:outline-none">
                        {COMPARE_METRICS.map((m) => (
                            <option key={m.key} value={m.key}
                                data-testid={`corp-cmp-metric-opt-${m.key}`}>{cmpLabel(m)}</option>
                        ))}
                    </select>
                </div>
                <div className="h-56" data-testid="corp-cmp-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="restaurant_name" tick={{ fontSize: 12, fill: "#525252" }}
                                axisLine={{ stroke: "#e5e5e5" }} tickLine={false} />
                            <YAxis tick={{ fontSize: 12, fill: "#737373" }}
                                axisLine={false} tickLine={false} />
                            <Tooltip
                                formatter={(v) => fmt(v)}
                                cursor={{ fill: "rgba(0,0,0,0.03)" }}
                                contentStyle={{ borderRadius: 12, border: "1px solid #e5e5e5",
                                                 fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }} />
                            <Bar dataKey="value" name={cmpLabel(spec)} radius={[8, 8, 0, 0]} isAnimationActive={false}>
                                {data.map((d) => (
                                    <Cell key={d.restaurant_id}
                                          fill={colorMap[d.restaurant_id] || "#94a3b8"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-4 flex flex-wrap gap-4" data-testid="corp-cmp-legend">
                    {data.map((d) => (
                        <div key={d.restaurant_id}
                             data-testid={`corp-cmp-legend-${d.restaurant_id}`}
                             className="flex items-center gap-2 text-[13px] text-neutral-700">
                            <ColorDot color={colorMap[d.restaurant_id] || "#94a3b8"} />
                            <span className="font-medium">{d.restaurant_name}</span>
                            <span className="text-neutral-500 tabular-nums">{fmt(d.value)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PerformanceTrend({ dash, metric, setMetric, selectedRid, colorMap }) {
    const spec = TREND_METRICS.find((m) => m.key === metric) || TREND_METRICS[0];
    const isAll = !selectedRid || selectedRid === "all";
    // Multi-line series (All Restaurants) — one line per restaurant.
    // Backed by the per-restaurant trend payload the backend produces
    // in the same `/api/corporate/dashboard` request. The aggregate
    // `trend` contract is preserved and used elsewhere on the page.
    const trendByRestaurant = dash?.trend_by_restaurant;
    const seriesList = useMemo(() => trendByRestaurant || [], [trendByRestaurant]);
    // Union of bucket keys across all series so lines share an X axis.
    const bucketKeys = useMemo(() => {
        const set = new Set();
        for (const s of seriesList) {
            for (const p of (s.points || [])) set.add(p.bucket_key);
        }
        return [...set].sort();
    }, [seriesList]);
    // Chart rows: {bucket_key, [restaurant_id]: value, ...}
    const multiRows = useMemo(() => {
        const byRid = {};
        for (const s of seriesList) {
            byRid[s.restaurant_id] = {};
            for (const p of (s.points || [])) {
                byRid[s.restaurant_id][p.bucket_key] = p[spec.key];
            }
        }
        return bucketKeys.map((k) => {
            const row = { bucket_key: k };
            for (const s of seriesList) {
                row[s.restaurant_id] = byRid[s.restaurant_id][k] ?? null;
            }
            return row;
        });
    }, [seriesList, bucketKeys, spec.key]);
    // Single-restaurant rows (unchanged behaviour).
    const singlePoints = (dash?.trend || []).map((p) => ({
        bucket_key: p.bucket_key,
        value: p[spec.key],
    }));
    const lineColor = (selectedRid && selectedRid !== "all"
                          && colorMap[selectedRid])
                         || "#0f172a";
    const fmtTip = (v) => (v == null ? "—"
        : spec.kind === "pct" ? `${Number(v).toFixed(1)}%`
        : spec.kind === "int" ? Number(v).toLocaleString("en-US")
        : spec.kind === "hours" ? `${Number(v).toFixed(1)} h`
        : `€ ${FMT_NUM(v)}`);
    return (
        <section data-testid="corporate-performance-trend"
                 data-mode={isAll ? "all" : "single"}>
            <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 h-full">
                <div className="flex items-baseline justify-between mb-3 gap-2">
                    <div className="min-w-0">
                        <h3 className="text-[13.5px] font-semibold text-neutral-900">{i18n.t("reports.performanceTrend", "Performance Trend")}</h3>
                        <p className="text-[11px] text-neutral-500 truncate">
                            {i18n.t("reports.groupingLabel", { defaultValue: "Grouping: {{g}}.", g: dash?.filters?.grouping ? i18n.t(`reports.grouping.${dash.filters.grouping}`, dash.filters.grouping) : "—" })}
                        </p>
                    </div>
                    <select data-testid="trend-metric"
                        value={metric} onChange={(e) => setMetric(e.target.value)}
                        className="h-8 px-2 border border-neutral-200 rounded-full text-[12px] bg-white text-neutral-800 focus:outline-none">
                        {TREND_METRICS.map((m) => (
                            <option key={m.key} value={m.key} data-testid={`trend-metric-opt-${m.key}`}>
                                {metricLabel(m)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="h-56" data-testid="trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={isAll ? multiRows : singlePoints}
                                    margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="bucket_key" tick={{ fontSize: 12, fill: "#525252" }}
                                axisLine={{ stroke: "#e5e5e5" }} tickLine={false} />
                            <YAxis tick={{ fontSize: 12, fill: "#737373" }}
                                axisLine={false} tickLine={false} />
                            <Tooltip
                                formatter={(v, name) => [fmtTip(v),
                                    isAll ? (seriesList.find((s) => s.restaurant_id === name)?.restaurant_name || name)
                                          : metricLabel(spec)]}
                                contentStyle={{ borderRadius: 12, border: "1px solid #e5e5e5",
                                                 fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }} />
                            {isAll
                                ? seriesList.map((s) => (
                                    <Line key={s.restaurant_id}
                                        type="monotone"
                                        dataKey={s.restaurant_id}
                                        name={s.restaurant_id}
                                        stroke={colorMap[s.restaurant_id] || "#94a3b8"}
                                        strokeWidth={2.25}
                                        dot={{ r: 2, fill: colorMap[s.restaurant_id] || "#94a3b8" }}
                                        activeDot={{ r: 5 }}
                                        isAnimationActive={false}
                                        connectNulls={false} />
                                ))
                                : (
                                    <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2.25}
                                        dot={{ r: 2, fill: lineColor }} activeDot={{ r: 5 }}
                                        isAnimationActive={false} connectNulls={false} />
                                )
                            }
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                {isAll && seriesList.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-4"
                         data-testid="trend-multi-legend">
                        {seriesList.map((s) => (
                            <div key={s.restaurant_id}
                                 data-testid={`trend-multi-legend-${s.restaurant_id}`}
                                 className="flex items-center gap-2 text-[13px] text-neutral-700">
                                <ColorDot color={colorMap[s.restaurant_id] || "#94a3b8"} />
                                <span className="font-medium">{s.restaurant_name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function RestaurantComparisonTable({ dash, colorMap }) {
    // The table lists per-restaurant KPI values. Sort is a user
    // inspection aid only — never used to derive any label / badge.
    const [sortKey, setSortKey] = useState("restaurant_name");
    const [sortDir, setSortDir] = useState("asc");
    const rows = useMemo(() => {
        const rs = [...(dash?.restaurant_comparison || [])];
        rs.sort((a, b) => {
            const av = a[sortKey]; const bv = b[sortKey];
            const cmp = av == null ? 1 : bv == null ? -1
                : (typeof av === "number" ? av - bv
                    : String(av).localeCompare(String(bv)));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return rs;
    }, [dash, sortKey, sortDir]);
    const toggle = (k) => {
        if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(k); setSortDir("asc"); }
    };
    const th = (k, label, right) => (
        <th key={k}
            className={`px-3 py-2 font-medium text-[10.5px] uppercase tracking-[0.06em] text-neutral-500 cursor-pointer select-none hover:text-neutral-800 transition-colors whitespace-nowrap ${right ? "text-right" : ""}`}
            data-testid={`corp-cmp-th-${k}`}
            onClick={() => toggle(k)}>
            {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </th>
    );
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : kind === "int" ? Number(v).toLocaleString("en-US")
        : kind === "hours" ? `${Number(v).toFixed(1)} h`
        : `€\u00A0${FMT_NUM(v)}`);
    return (
        <section className="mb-4" data-testid="corporate-restaurant-comparison-table-section">
            <header className="flex flex-wrap items-baseline justify-between gap-2 mb-2"
                     data-testid="corp-detail-header">
                <div className="flex items-baseline gap-3">
                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-neutral-400">
                        Detail
                    </span>
                    <h3 className="font-display text-lg font-semibold text-neutral-900 leading-tight">
                        {i18n.t("reports.l.Restaurant_comparison", "Restaurant comparison")}
                    </h3>
                </div>
                <span className="text-[11px] text-neutral-400" title={i18n.t("reports.l.Sort_is_a_user_aid_only_Chart_above_is_for_fast_understanding_this_tab", "Sort is a user aid only. Chart above is for fast understanding; this table is for exact inspection.")}>
                    {i18n.t("reports.l.Click_a_column_header_to_sort", "Click a column header to sort")}
                </span>
            </header>
            <div className="rounded-2xl bg-white border border-neutral-200/70 overflow-hidden">
                <div className="ms-rt-wrap">
                <table className="w-full text-[13px] ms-rt" data-testid="corp-restaurant-comparison">
                    <thead>
                        <tr className="text-left border-b border-neutral-100">
                            {th("restaurant_name", i18n.t("reports.l.Restaurant", "Restaurant"), false)}
                            {th("net_sales", i18n.t("reports.l.Net_Sales", "Net Sales"), true)}
                            {th("purchases_net", i18n.t("reports.l.Purchases", "Purchases"), true)}
                            {th("delivery_fee_net", i18n.t("reports.l.Delivery_Fee", "Delivery Fee"), true)}
                            {th("total_expense_net", i18n.t("reports.l.Total_Expense", "Total Expense"), true)}
                            {th("actual_cogs", i18n.t("reports.l.Actual_COGS", "Actual COGS"), true)}
                            {th("food_cost_pct", i18n.t("reports.l.Food_Cost_PCT", "Food Cost %"), true)}
                            {th("kate_pct", i18n.t("reports.l.Kate_PCT", "Kate %"), true)}
                            {th("waste_net", i18n.t("reports.l.Waste_EUR", "Waste €"), true)}
                            {th("waste_pct_of_purchases", i18n.t("reports.l.Waste_PCT_Purch", "Waste % Purch."), true)}
                            {th("customers", i18n.t("reports.l.Customers", "Customers"), true)}
                            {th("average_spend_per_customer", i18n.t("reports.l.Avg_Cust", "Avg / Cust."), true)}
                            {th("worked_hours", i18n.t("reports.l.Hours", "Hours"), true)}
                            {th("teho", i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)"), true)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-3 py-4 text-sm text-neutral-500"
                                    data-testid="corp-restaurant-comparison-empty" colSpan={10}>
                                    {i18n.t("reports.l.No_restaurants_available", "No restaurants available.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.restaurant_id} data-testid={`corp-cmp-row-${r.restaurant_id}`}
                                className="hover:bg-neutral-50/60 transition-colors">
                                <td className="px-3 py-2 font-medium text-neutral-800 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-2">
                                        <ColorDot color={colorMap[r.restaurant_id] || "#94a3b8"} />
                                        {r.restaurant_name}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.net_sales, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.purchases_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`corp-cmp-fee-${r.restaurant_id}`}>{cell(r.delivery_fee_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`corp-cmp-expense-${r.restaurant_id}`}>{cell(r.total_expense_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.actual_cogs, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.food_cost_pct, "pct")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.kate_pct, "pct")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.waste_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.waste_pct_of_purchases, "pct")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.customers, "int")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.average_spend_per_customer, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.worked_hours, "hours")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(r.teho, "eur")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>
        </section>
    );
}

// Restaurant chip row — quick-select map for the existing `rid` filter.
// Renders one chip per restaurant plus an "All Restaurants" chip. Uses
// the same stable identity color as every other Corporate chart. The
// underlying filter behaviour, contract, and permission (disabledAll)
// are preserved: this is presentation-only for a value that was already
// bound to `rid` via the pre-existing dropdown.
function LocationChips({ rid, setRid, restaurants, colorMap, disabledAll }) {
    const chip = (active) =>
        "inline-flex items-center gap-2 h-8 px-3 rounded-full border text-[13px] font-medium transition-colors " +
        (active
            ? "bg-neutral-900 text-white border-neutral-900"
            : "bg-white text-neutral-800 border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50");
    return (
        <div className="flex flex-wrap items-center gap-2"
             data-testid="corp-locations-chips">
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mr-1">
                Locations
            </span>
            <button type="button"
                onClick={() => { if (!disabledAll) setRid("all"); }}
                disabled={disabledAll}
                data-testid="corp-chip-all"
                aria-pressed={rid === "all"}
                className={chip(rid === "all") + (disabledAll ? " opacity-50 cursor-not-allowed" : "")}>
                {i18n.t("reports.l.All_Restaurants", "All Restaurants")}
            </button>
            {restaurants.map((r) => (
                <button key={r.id} type="button"
                    data-testid={`corp-chip-${r.id}`}
                    aria-pressed={rid === r.id}
                    onClick={() => setRid(r.id)}
                    className={chip(rid === r.id)}>
                    <ColorDot color={colorMap[r.id] || "#94a3b8"} />
                    <span>{r.name}</span>
                </button>
            ))}
        </div>
    );
}

// Compact filter strip — same controls as the original CorporateFilters
// (Restaurant / From / To / Grouping / Comparison), same testids, same
// state hooks. Restaurant is expressed as the sibling `LocationChips`
// row so the dropdown is not repeated. From/To/Grouping/Comparison are
// laid out as a single horizontal strip matching the approved desktop
// composition.
function CorporateFilters({ rid, setRid, from, setFrom, to, setTo,
                              grouping, setGrouping,
                              comparison, setComparison,
                              restaurants, disabledAll, colorMap,
                              open, setOpen }) {
    const seg = (active) =>
        "px-3 h-8 rounded-full text-[12.5px] font-medium transition-colors " +
        (active
            ? "bg-white text-neutral-900 shadow-sm"
            : "text-neutral-600 hover:text-neutral-900");
    return (
        <section className="mb-4" data-testid="corporate-filters">
            <div className="mb-3">
                <LocationChips
                    rid={rid} setRid={setRid} restaurants={restaurants}
                    colorMap={colorMap || {}} disabledAll={disabledAll} />
            </div>
            {/* Hidden accessible mirror — preserves original testids for
                external tests / handlers targeting the underlying select
                elements. Not user-visible. */}
            <div className="sr-only" aria-hidden="true">
                <select data-testid="corp-filter-restaurant"
                    value={rid} onChange={(e) => setRid(e.target.value)}>
                    <option value="all" disabled={disabledAll}
                        data-testid="corp-filter-restaurant-all">{i18n.t("reports.l.All_Restaurants", "All Restaurants")}</option>
                    {restaurants.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </select>
                <select data-testid="corp-filter-grouping"
                    value={grouping} onChange={(e) => setGrouping(e.target.value)}>
                    <option value="daily">{i18n.t("reports.l.Daily", "Daily")}</option>
                    <option value="weekly">{i18n.t("reports.l.Weekly", "Weekly")}</option>
                    <option value="monthly">{i18n.t("reports.l.Monthly", "Monthly")}</option>
                </select>
                <select data-testid="corp-filter-comparison"
                    value={comparison} onChange={(e) => setComparison(e.target.value)}>
                    <option value="none">{i18n.t("reports.l.None", "None")}</option>
                    <option value="previous_period">{i18n.t("reports.l.Previous_Period", "Previous Period")}</option>
                    <option value="previous_year">{i18n.t("reports.l.Previous_Year", "Previous Year")}</option>
                </select>
            </div>
            {open && (
                <div className="rounded-2xl bg-white border border-neutral-200/70 px-5 py-4 flex flex-wrap items-end gap-4"
                     data-testid="corp-filters-popover">
                    <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-1.5">{i18n.t("reports.from", "From")}</label>
                        <input type="date" data-testid="corp-filter-from"
                            value={from} onChange={(e) => setFrom(e.target.value)}
                            className="h-9 px-3 border border-neutral-200 rounded-lg text-[13px] bg-white text-neutral-800 focus:ring-2 focus:ring-neutral-900/10 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-1.5">{i18n.t("reports.to", "To")}</label>
                        <input type="date" data-testid="corp-filter-to"
                            value={to} onChange={(e) => setTo(e.target.value)}
                            className="h-9 px-3 border border-neutral-200 rounded-lg text-[13px] bg-white text-neutral-800 focus:ring-2 focus:ring-neutral-900/10 focus:outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-1.5">{i18n.t("reports.groupingTitle", "Grouping")}</label>
                        <div role="tablist" className="inline-flex items-center rounded-full bg-neutral-100 p-1 gap-0.5">
                            {[
                                { v: "daily",   l: i18n.t("reports.l.Daily", "Daily") },
                                { v: "weekly",  l: i18n.t("reports.l.Weekly", "Weekly") },
                                { v: "monthly", l: i18n.t("reports.l.Monthly", "Monthly") },
                            ].map((o) => (
                                <button key={o.v} type="button"
                                    role="tab" aria-selected={grouping === o.v}
                                    data-testid={`corp-filter-grouping-${o.v}`}
                                    onClick={() => setGrouping(o.v)}
                                    className={seg(grouping === o.v)}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10.5px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-1.5">{i18n.t("reports.comparison", "Comparison")}</label>
                        <div role="tablist" className="inline-flex items-center rounded-full bg-neutral-100 p-1 gap-0.5">
                            {[
                                { v: "none",            l: i18n.t("reports.l.None", "None") },
                                { v: "previous_period", l: i18n.t("reports.l.Prev_Period", "Prev Period") },
                                { v: "previous_year",   l: i18n.t("reports.l.Prev_Year", "Prev Year") },
                            ].map((o) => (
                                <button key={o.v} type="button"
                                    role="tab" aria-selected={comparison === o.v}
                                    data-testid={o.v === "previous_period"
                                        ? "corp-filter-comparison-prev-period"
                                        : (o.v === "previous_year"
                                            ? "corp-filter-comparison-prev-year"
                                            : "corp-filter-comparison-none")}
                                    onClick={() => setComparison(o.v)}
                                    className={seg(comparison === o.v)}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button type="button"
                        onClick={() => setOpen(false)}
                        data-testid="corp-filter-close"
                        className="ml-auto h-9 px-3 rounded-lg border border-neutral-200 text-[12.5px] font-semibold text-neutral-600 hover:bg-neutral-50">
                        Done
                    </button>
                </div>
            )}
        </section>
    );
}

// --------------------------------------------------------------------
// Approved desktop panels — Net Sales by Day (stacked), Top Waste
// Items and Recent Inventory Counts. Each panel is bound STRICTLY to
// an existing MiseOne data source: no new backend endpoint, no new
// formula, no fabricated values. Missing values keep their canonical
// em-dash rendering; when an entire series is unavailable the panel
// surfaces a factual empty state instead.
// --------------------------------------------------------------------

// Panel A · Net Sales by Day — Recharts stacked bar chart.
// * Source: `dash.trend_by_restaurant[i].points[]` with `net_sales`
//   values, one series per restaurant.
// * All Restaurants → stacked bars (identity colors, one segment per
//   restaurant per bucket).
// * Single restaurant → the same chart with only that restaurant's
//   series present (single-color bar per bucket).
// * Follows the current grouping / date range / restaurant filter.
function NetSalesByDayChart({ dash, colorMap, selectedRid }) {
    const grouping = dash?.filters?.grouping || "daily";
    const seriesList = dash?.trend_by_restaurant || [];
    const filtered = (selectedRid && selectedRid !== "all")
        ? seriesList.filter((s) => s.restaurant_id === selectedRid)
        : seriesList;
    const bucketKeys = useMemo(() => {
        const set = new Set();
        for (const s of filtered) {
            for (const p of (s.points || [])) set.add(p.bucket_key);
        }
        return [...set].sort();
    }, [filtered]);
    const rows = useMemo(() => {
        const byRid = {};
        for (const s of filtered) {
            byRid[s.restaurant_id] = {};
            for (const p of (s.points || [])) {
                byRid[s.restaurant_id][p.bucket_key] = p.net_sales;
            }
        }
        return bucketKeys.map((k) => {
            const row = { bucket_key: k };
            for (const s of filtered) {
                row[s.restaurant_id] = byRid[s.restaurant_id][k] ?? null;
            }
            return row;
        });
    }, [filtered, bucketKeys]);
    const empty = rows.length === 0 || filtered.length === 0;
    const groupingLabel = grouping === "monthly"
        ? i18n.t("reports.l.Monthly_grouping", "Monthly grouping")
        : grouping === "weekly" ? i18n.t("reports.l.Weekly_grouping", "Weekly grouping") : i18n.t("reports.l.Daily_grouping", "Daily grouping");
    const primaryTitle = grouping === "monthly"
        ? i18n.t("reports.l.Net_Sales_by_Month", "Net Sales by Month")
        : grouping === "weekly" ? i18n.t("reports.l.Net_Sales_by_Week", "Net Sales by Week") : i18n.t("reports.l.Net_Sales_by_Day", "Net Sales by Day");
    return (
        <section data-testid="corp-net-sales-by-day">
            <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 h-full">
                <div className="flex items-baseline justify-between mb-3 gap-2">
                    <div className="min-w-0">
                        <h3 className="text-[13.5px] font-semibold text-neutral-900">{primaryTitle}</h3>
                        <p className="text-[11px] text-neutral-500 truncate">
                            {groupingLabel} · grouped by restaurant identity.
                        </p>
                    </div>
                </div>
                <div className="h-64" data-testid="corp-net-sales-chart">
                    {empty ? (
                        <div className="h-full flex items-center justify-center text-[12.5px] text-neutral-500 text-center px-4"
                             data-testid="corp-net-sales-empty">
                            {i18n.t("reports.l.No_Net_Sales_points_in_the_selected_window", "No Net Sales points in the selected window.")}
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={rows}
                                barCategoryGap="18%"
                                barGap={2}
                                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="bucket_key"
                                    tick={{ fontSize: 11, fill: "#525252" }}
                                    axisLine={{ stroke: "#e5e5e5" }} tickLine={false}
                                    interval="preserveStartEnd" minTickGap={16} />
                                <YAxis tick={{ fontSize: 11, fill: "#737373" }}
                                    axisLine={false} tickLine={false}
                                    tickFormatter={(v) => v == null ? "—"
                                        : Math.abs(v) >= 1000 ? `€${Math.round(v/1000)}k` : `€${Math.round(v)}`} />
                                <Tooltip
                                    content={({ active, payload, label }) => {
                                        if (!active || !payload || !payload.length) return null;
                                        const total = payload.reduce(
                                            (s, p) => s + (Number(p.value) || 0), 0);
                                        return (
                                            <div className="rounded-xl border border-neutral-200 bg-white p-3 shadow-md"
                                                 data-testid="corp-net-sales-tooltip">
                                                <div className="text-[11px] font-semibold text-neutral-800 mb-2">
                                                    {label}
                                                </div>
                                                {payload.map((p) => {
                                                    const s = filtered.find((x) => x.restaurant_id === p.dataKey);
                                                    return (
                                                        <div key={p.dataKey}
                                                             data-testid={`corp-net-sales-tt-${p.dataKey}`}
                                                             className="flex items-center justify-between gap-4 text-[12px]">
                                                            <span className="flex items-center gap-2 text-neutral-700">
                                                                <ColorDot color={p.color} />
                                                                {s?.restaurant_name || p.dataKey}
                                                            </span>
                                                            <span className="font-medium tabular-nums text-neutral-900">
                                                                {p.value == null ? "—" : `€\u00A0${FMT_NUM(p.value)}`}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                <div className="mt-2 pt-2 border-t border-neutral-100 flex items-center justify-between text-[12px]"
                                                     data-testid="corp-net-sales-tt-total">
                                                    <span className="text-neutral-500">{i18n.t("reports.l.Combined_total", "Combined total")}</span>
                                                    <span className="font-semibold tabular-nums text-neutral-900">
                                                        {total == 0 ? "—" : `€\u00A0${FMT_NUM(total)}`}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }} />
                                {filtered.map((s) => (
                                    <Bar key={s.restaurant_id}
                                         dataKey={s.restaurant_id}
                                         name={s.restaurant_id}
                                         fill={colorMap[s.restaurant_id] || "#94a3b8"}
                                         radius={[3, 3, 0, 0]}
                                         isAnimationActive={false} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap gap-3"
                     data-testid="corp-net-sales-legend">
                    {filtered.map((s) => (
                        <div key={s.restaurant_id}
                             data-testid={`corp-net-sales-legend-${s.restaurant_id}`}
                             className="flex items-center gap-2 text-[12px] text-neutral-700">
                            <ColorDot color={colorMap[s.restaurant_id] || "#94a3b8"} />
                            <span className="font-medium">{s.restaurant_name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Panel B · Top Waste Items — horizontal ordered bars in € (est_cost).
// * Source: the EXISTING `/api/reports/waste` endpoint, which returns
//   `by_dish` per single restaurant. There is no company-wide per-item
//   waste aggregate in the current backend; per the STRICT policy we
//   NEVER invent one client-side. When "All Restaurants" is selected
//   the panel surfaces a factual empty state instead.
// * Follows the current restaurant / from / to filter state.
function TopWasteItemsPanel({ rid: dashRid, from, to }) {
    const { restaurants, restaurantId } = useApp();
    const [ownRid, setOwnRid] = useState(restaurantId || "");
    useEffect(() => { if (!ownRid && restaurantId) setOwnRid(restaurantId); }, [restaurantId, ownRid]);
    const pickHere = !dashRid || dashRid === "all";
    const rid = pickHere ? ownRid : dashRid;
    const isSingle = rid && rid !== "all";
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    useEffect(() => {
        if (!isSingle) { setItems(null); setErr(null); return; }
        let cancelled = false;
        setLoading(true); setErr(null);
        api.get("/reports/waste", { params: { from, to, restaurant_id: rid } })
            .then(({ data }) => {
                if (cancelled) return;
                const rows = [...(data?.by_dish || [])]
                    .map((r) => ({
                        name: r.dish_name,
                        value: Number(r.est_cost) || 0,
                    }))
                    .filter((r) => r.value > 0)
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 5);
                setItems(rows);
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(detailOr(e?.response?.data?.detail, i18n.t("reports.l.Failed_to_load", "Failed to load")));
                setItems([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isSingle, rid, from, to]);
    return (
        <section data-testid="corp-top-waste-items">
            <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 h-full">
                <div className="flex items-baseline justify-between mb-3 gap-2">
                    <h3 className="text-[13.5px] font-semibold text-neutral-900">{i18n.t("reports.l.Top_waste_items", "Top waste items")}</h3>
                    <span className="text-[11px] text-neutral-500">{i18n.t("reports.l.Selected_window", "Selected window")}</span>
                </div>
                {pickHere && (restaurants || []).length > 0 && (
                    <label className="mb-3 flex items-center gap-2 text-[12.5px] text-neutral-600"
                           onClick={(e) => e.stopPropagation()}>
                        {i18n.t("reports.l.Restaurant", "Restaurant")}
                        <select value={ownRid} onChange={(e) => setOwnRid(e.target.value)}
                                data-testid="corp-top-waste-restaurant"
                                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-[12.5px] text-neutral-900">
                            <option value="">—</option>
                            {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </label>
                )}
                {pickHere && isSingle && (
                    <p className="mb-3 text-[12px] font-medium text-neutral-700" data-testid="corp-top-waste-scope">
                        {i18n.t("reports.l.Top_waste_scope_single", { defaultValue: "Showing {{name}} only · not a total for all restaurants",
                            name: ((restaurants || []).find((r) => r.id === rid) || {}).name || rid })}
                    </p>
                )}
                {!isSingle && (
                    <div className="text-[12.5px] text-neutral-600 leading-relaxed"
                         data-testid="corp-top-waste-need-restaurant">
                        <p>{i18n.t("reports.l.Top_waste_items_are_shown_per_restaurant", "Top waste items are shown per restaurant.")}</p>
                        <p className="mt-2 text-neutral-500">{i18n.t("reports.l.Pick_a_single_location_above_to_see_the_list", "Pick a single location above to see the list.")}</p>
                    </div>
                )}
                {isSingle && loading && (
                    <div className="text-[12.5px] text-neutral-500"
                         data-testid="corp-top-waste-loading">{i18n.t("common.loading", "Loading…")}</div>
                )}
                {isSingle && !loading && err && (
                    <div className="text-[12.5px] text-rose-700"
                         data-testid="corp-top-waste-err">{String(err)}</div>
                )}
                {isSingle && !loading && !err && items && items.length === 0 && (
                    <div className="text-[12.5px] text-neutral-500"
                         data-testid="corp-top-waste-empty">
                        {i18n.t("reports.l.No_waste_items_recorded_in_this_window_for_the_selected_restaurant", "No waste items recorded in this window for the selected restaurant.")}
                    </div>
                )}
                {isSingle && !loading && !err && items && items.length > 0 && (
                    <div className="kop-card ms-rt-wrap">
                        <table className="w-full text-[13px] ms-rt" data-testid="corp-top-waste-list">
                            <thead>
                                <tr className="text-left text-neutral-600">
                                    <th className="px-3 py-2 font-semibold text-right w-10">#</th>
                                    <th className="px-3 py-2 font-semibold">{i18n.t("reports.l.Item", "Item")}</th>
                                    <th className="px-3 py-2 font-semibold text-right">{i18n.t("reports.l.Est_cost", "Est. cost")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((r, i) => (
                                    <tr key={r.name + i} data-testid={`corp-top-waste-row-${i}`}>
                                        <td className="px-3 py-2 text-right text-neutral-500">{i + 1}</td>
                                        <td className="px-3 py-2 font-medium text-neutral-800 whitespace-normal">{r.name}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-neutral-900">€ {FMT_NUM(r.value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
}

// Panel C · Recent Inventory Counts — completed physical inventory
// counts per restaurant, using the EXISTING `/api/inventory-counts`
// route (list_inventory_counts). This route returns the raw completed
// counts and is independent of any COGS boundary — a completed count
// appears even when there is no earlier opening inventory. The KPI
// row above keeps Actual COGS / Food Cost / Kate at "—" independently.
//
// IMPORTANT: `inventory_counts.total_value` is the item-price rollup
// (SUM of `qty × resolved.price`). The Corporate accounting engine
// deliberately does NOT use this scalar (see server.py:10742) and
// derives the authoritative NET from
// `_build_inventory_accounting_dataset(...).meta.total_net`. This
// panel therefore labels the figure as "Inventory value" (matching
// the Inventory screen's "Total value" column) — it never substitutes
// this figure into Corporate COGS. Rows show only what the record
// actually saved: restaurant, canonical `inventory_month` (with
// legacy `year`+`month` fallback), completed status, inventory value.
// No opening/closing carry-over inference is displayed here — that
// remains the Corporate calculation engine's job upstream.
function RecentInventoryCountsPanel({ restaurants, colorMap,
                                        selectedRid, from, to }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const queryList = useMemo(() => {
        if (selectedRid && selectedRid !== "all") {
            const hit = (restaurants || [])
                .find((r) => r.id === selectedRid);
            return hit ? [hit] : [];
        }
        return restaurants || [];
    }, [selectedRid, restaurants]);

    useEffect(() => {
        if (queryList.length === 0) { setRows([]); return; }
        let cancelled = false;
        setLoading(true); setErr(null);
        Promise.all(queryList.map((r) =>
            api.get("/inventory-counts", { params: { restaurant_id: r.id } })
                .then(({ data }) => (data || []).map((c) => ({
                    ...c,
                    _rest_name: r.name,
                    _rest_id: r.id,
                })))
                .catch(() => [])))
            .then((chunks) => {
                if (cancelled) return;
                const all = [].concat(...chunks);
                // Canonical inventory-month key. Prefer explicit
                // `inventory_month`; fall back to legacy `year`+`month`
                // (int pair) so pre-2026-02 records remain first-class.
                // NEVER infer the month from `count_date`.
                const monthKey = (c) => {
                    if (typeof c.inventory_month === "string"
                          && c.inventory_month.length >= 7) {
                        return c.inventory_month.slice(0, 7);
                    }
                    if (Number.isInteger(c.year)
                          && Number.isInteger(c.month)
                          && c.month >= 1 && c.month <= 12) {
                        return `${c.year}-${String(c.month).padStart(2, "0")}`;
                    }
                    return null;
                };
                const fromM = (from || "").slice(0, 7);
                const toM   = (to   || "").slice(0, 7);
                const filtered = all.filter((c) => {
                    if (c.status !== "completed") return false;
                    const mk = monthKey(c);
                    if (!mk) return false;
                    if (fromM && mk < fromM) return false;
                    if (toM   && mk > toM)   return false;
                    return true;
                });
                filtered.sort((a, b) => {
                    const mk = String(monthKey(b)).localeCompare(String(monthKey(a)));
                    if (mk !== 0) return mk;
                    return String(a._rest_name).localeCompare(String(b._rest_name));
                });
                setRows(filtered.slice(0, 6));
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(detailOr(e?.response?.data?.detail, i18n.t("reports.l.Failed_to_load", "Failed to load")));
                setRows([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [queryList, from, to]);

    const monthKey = (c) => {
        if (typeof c.inventory_month === "string"
              && c.inventory_month.length >= 7) {
            return c.inventory_month.slice(0, 7);
        }
        if (Number.isInteger(c.year)
              && Number.isInteger(c.month)
              && c.month >= 1 && c.month <= 12) {
            return `${c.year}-${String(c.month).padStart(2, "0")}`;
        }
        return "—";
    };
    return (
        <section data-testid="corp-recent-inventory-counts">
            <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 h-full">
                <div className="flex items-baseline justify-between mb-3 gap-2">
                    <h3 className="text-[13.5px] font-semibold text-neutral-900">{i18n.t("reports.l.Recent_inventory_counts", "Recent inventory counts")}</h3>
                    <span className="text-[11px] text-neutral-500">{i18n.t("reports.l.Completed_physical_inventory", "Completed physical inventory")}</span>
                </div>
                {loading && (
                    <div className="text-[12.5px] text-neutral-500"
                         data-testid="corp-recent-inv-loading">{i18n.t("common.loading", "Loading…")}</div>
                )}
                {!loading && err && (
                    <div className="text-[12.5px] text-rose-700"
                         data-testid="corp-recent-inv-err">{String(err)}</div>
                )}
                {!loading && !err && rows.length === 0 && (
                    <div className="text-[12.5px] text-neutral-500 leading-relaxed"
                         data-testid="corp-recent-inv-empty">
                        {i18n.t("reports.l.No_completed_physical_inventory_in_the_selected_window", "No completed physical inventory in the selected window.")}
                    </div>
                )}
                {!loading && !err && rows.length > 0 && (
                    <ul className="flex flex-col gap-3"
                         data-testid="corp-recent-inv-list">
                        {rows.map((r) => {
                            const mk = monthKey(r);
                            return (
                                <li key={r.id}
                                    data-testid={`corp-recent-inv-row-${r._rest_id}-${mk}`}
                                    className="flex items-start gap-3">
                                    <ColorDot color={colorMap[r._rest_id] || "#94a3b8"} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline justify-between gap-2 text-[12.5px]">
                                            <span className="font-medium text-neutral-800 truncate">
                                                {r._rest_name}
                                            </span>
                                            <span className="font-semibold tabular-nums text-neutral-900"
                                                  data-testid={`corp-recent-inv-value-${r._rest_id}-${mk}`}>
                                                {r.total_value == null ? "—" : `€ ${FMT_NUM(r.total_value)}`}
                                            </span>
                                        </div>
                                        <div className="text-[11.5px] text-neutral-500 leading-snug flex items-center gap-2">
                                            <span data-testid={`corp-recent-inv-month-${r._rest_id}-${mk}`}>{mk}</span>
                                            <span aria-hidden="true">·</span>
                                            <span>{i18n.t("reports.completed", "Completed")}</span>
                                            <span aria-hidden="true">·</span>
                                            <span className="text-neutral-400">{i18n.t("reports.l.Inventory_value", "Inventory value")}</span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <p className="mt-4 text-[11px] text-neutral-400 leading-snug">
                    {i18n.t("reports.invValueNote", 'Inventory value shown as saved on the count (item-price rollup). Actual COGS in the KPIs above uses the Corporate accounting engine and stays "—" until a preceding opening count exists — completed counts are not hidden by that state.')}
                </p>
            </div>
        </section>
    );
}

// Waste snapshot card — third middle-row panel matching the approved
// desktop composition. Uses ONLY existing supported Corporate KPI
// fields (dash.kpi.waste_net, waste_pct_of_purchases, purchases_net).
// No new API call, no new formula, no fabrication. Missing values keep
// their canonical "—".
function WasteSnapshotCard({ dash }) {
    const kpi = dash?.kpi || {};
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : `€ ${FMT_NUM(v)}`);
    const waste = kpi.waste_net;
    const purch = kpi.purchases_net;
    const share = (waste != null && purch && purch > 0)
        ? Math.min(100, (Number(waste) / Number(purch)) * 100)
        : null;
    return (
        <div className="rounded-2xl bg-white border border-neutral-200/70 p-5"
             data-testid="corp-waste-snapshot">
            <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[13.5px] font-semibold text-neutral-900">
                    {i18n.t("reports.l.Waste_snapshot", "Waste snapshot")}
                </h3>
                <span className="text-[11px] text-neutral-500">{i18n.t("reports.l.Selected_window", "Selected window")}</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-neutral-500">
                {i18n.t("reports.l.Waste_EUR", "Waste €")}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-900 leading-none"
                 data-testid="corp-waste-snapshot-waste-net">
                {cell(waste, "eur")}
            </div>
            <div className="mt-4 flex items-baseline justify-between">
                <span className="text-[11.5px] text-neutral-500">{i18n.t("reports.l.Waste_PCT_of_Purchases", "Waste % of Purchases")}</span>
                <span className="text-[13px] font-semibold tabular-nums text-neutral-900"
                      data-testid="corp-waste-snapshot-waste-pct">
                    {cell(kpi.waste_pct_of_purchases, "pct")}
                </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-neutral-900 transition-all"
                     style={{ width: `${share == null ? 0 : share}%` }}
                     data-testid="corp-waste-snapshot-bar" />
            </div>
            <div className="mt-4 flex items-baseline justify-between">
                <span className="text-[11.5px] text-neutral-500">{i18n.t("reports.l.Purchases_NET", "Purchases NET")}</span>
                <span className="text-[13px] font-semibold tabular-nums text-neutral-900"
                      data-testid="corp-waste-snapshot-purchases">
                    {cell(purch, "eur")}
                </span>
            </div>
            <p className="mt-4 text-[11px] text-neutral-400 leading-snug">
                {i18n.t("reports.l.Same_values_as_the_canonical_Waste_section_below_No_new_formula_is_app", "Same values as the canonical Waste section below. No new formula is applied here.")}
            </p>
        </div>
    );
}

function SalesCostAnalysis({ dash, variant }) {
    // Block 4 · Sales & Cost Analysis. Reuses the KPI values already
    // computed by the backend calculation engine and renders the
    // grouping-driven breakdown table. No new financial calculation is
    // performed in JavaScript — everything comes from `dash`.
    //
    // UX cleanup: the parent Corporate page now renders this component
    // twice — once as `variant="kpis-only"` at the top of the page
    // (the only KPI block, with 12 metrics and an expandable "How it
    // is calculated" section), and once as `variant="period-only"` at
    // the bottom of the main spine for the grouping-driven period
    // breakdown. Logic / data / testids are unchanged.
    const kpi = dash?.kpi || {};
    const rows = dash?.trend || [];
    const grouping = dash?.filters?.grouping || "daily";
    const showAccountingCols = grouping === "monthly";
    const groupingLabel = { daily: i18n.t("reports.l.Daily", "Daily"), weekly: i18n.t("reports.l.Weekly", "Weekly"), monthly: i18n.t("reports.l.Monthly", "Monthly") }[grouping] || grouping;
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : kind === "int" ? Number(v).toLocaleString("en-US")
        : `€ ${FMT_NUM(v)}`);
    // Purchase-based Margin (daily / weekly / monthly, computed from
    // the bucket's existing Net Sales and Purchases NET). This is a
    // DIFFERENT concept from COGS-based Kate — purchases recorded in
    // the bucket are not necessarily consumed inside the bucket. No
    // new data is fetched; this is a presentation-level derivation
    // of two canonical fields that are already in `dash.trend`.
    const purchaseMarginEur = (p) => (
        p.net_sales == null || p.purchases_net == null
            ? null
            : Number(p.net_sales) - Number(p.purchases_net)
    );
    const purchaseMarginPct = (p) => {
        if (p.net_sales == null || p.purchases_net == null) return null;
        if (Number(p.net_sales) === 0) return null;
        return (Number(p.net_sales) - Number(p.purchases_net))
                / Number(p.net_sales) * 100;
    };
    // Worked Hours is nullable only when no Daily Performance row is
    // recorded in the bucket; Teho follows the canonical Core rule
    // (SUM Net Sales ÷ SUM Worked Hours) applied in the backend —
    // never averaged across restaurants on the frontend.
    const hoursCell = (v) => (v == null ? "—" : `${Number(v).toFixed(1)} h`);
    // Compact "DD MMM" formatter for the Daily breakdown Day column
    // (year stays visible in the surrounding period context / filters).
    const fmtDayCompact = (key) => {
        if (!key || typeof key !== "string" || key.length < 10) return key;
        const d = new Date(`${key.slice(0, 10)}T00:00:00Z`);
        if (isNaN(d.getTime())) return key;
        const day = String(d.getUTCDate()).padStart(2, "0");
        const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
        return localizeDateNames(`${day} ${mon}`);
    };
    // Total row for the Daily breakdown. Reuses canonical missing-data
    // rules: only sum non-null values; "—" for empty columns and zero
    // or unavailable denominators; never average daily %/Teho/Avg Spend.
    const dailyTotals = (() => {
        const sumIf = (key) => {
            let sum = 0, any = false;
            for (const r of (rows || [])) {
                if (r && r[key] != null) { sum += Number(r[key]); any = true; }
            }
            return any ? sum : null;
        };
        const totGross  = sumIf("gross_sales");
        const totNet    = sumIf("net_sales");
        const totPurch  = sumIf("purchases_net");
        const totWaste  = sumIf("waste_net");
        const totHours  = sumIf("worked_hours");
        const totCust   = sumIf("customers");
        const totMarginEur = (totNet != null && totPurch != null)
            ? totNet - totPurch : null;
        const totMarginPct = (totMarginEur != null && totNet != null
                              && Number(totNet) > 0)
            ? (totMarginEur / totNet) * 100 : null;
        const totTeho = (totNet != null && totHours != null
                         && Number(totHours) > 0)
            ? totNet / totHours : null;
        const totAvgSpend = (totNet != null && totCust != null
                             && Number(totCust) > 0)
            ? totNet / totCust : null;
        return { totGross, totNet, totPurch, totWaste, totHours, totCust,
                 totMarginEur, totMarginPct, totTeho, totAvgSpend };
    })();
    const showKpis = variant !== "period-only";
    const showPeriod = variant !== "kpis-only";
    // --- Monthly target colouring for the KPI cards ------------------
    // Reuse the canonical Target vs Actual eligibility. Colour the
    // actual value in the Sales & Cost KPI cards only when the current
    // window represents EXACTLY ONE complete, closed calendar month
    // for a SINGLE restaurant, and only on metrics whose per-metric
    // status is "complete" with a target saved. Equality = met.
    // No Hours / Teho colouring. No company-aggregate colouring.
    // Waste € card stays untouched (no matching % denominator here).
    const filters = dash?.filters || {};
    const singleRestaurant = !!filters.restaurant_id && filters.restaurant_id !== "all";
    const lastDayOfIsoMonth = (ym) => {
        if (!ym || ym.length < 7) return null;
        const y = Number(ym.slice(0, 4)), m = Number(ym.slice(5, 7));
        const d = new Date(Date.UTC(y, m, 0));
        return d.toISOString().slice(0, 10);
    };
    const singleFullMonth = (() => {
        const f = filters.from, t = filters.to;
        if (!f || !t || f.length !== 10 || t.length !== 10) return null;
        if (f.slice(0, 7) !== t.slice(0, 7)) return null;
        if (f.slice(8, 10) !== "01") return null;
        const last = lastDayOfIsoMonth(f.slice(0, 7));
        if (!last || t !== last) return null;
        return f.slice(0, 7);
    })();
    const tvaRows = dash?.target_vs_actual?.by_restaurant_month || [];
    const tvaMatch = (singleRestaurant && singleFullMonth)
        ? tvaRows.find((r) => r.restaurant_id === filters.restaurant_id
                              && r.month === singleFullMonth)
        : null;
    const PER_METRIC_STATUS_KEY = {
        net_sales:     "net_sales_customers_status",
        customers:     "net_sales_customers_status",
        food_cost_pct: "food_cost_kate_status",
        kate_pct:      "food_cost_kate_status",
    };
    const METRIC_DIRECTION = {
        net_sales: "higher", customers: "higher",
        kate_pct:  "higher", food_cost_pct: "lower",
    };
    const METRIC_KIND = {
        net_sales: "eur", customers: "int",
        kate_pct:  "pct", food_cost_pct: "pct",
    };
    const diffKindFor = (name) => (
        name === "net_sales" ? "eur" :
        name === "customers" ? "int" :
        "pp"
    );
    const ppCell = (v) => (v == null ? "—" : `${Number(v).toFixed(1)} pp`);
    const monthlyTargetProps = (metricName) => {
        if (!tvaMatch || !tvaMatch.has_target) return { tone: "neutral", title: undefined };
        const entry = tvaMatch[metricName];
        if (!entry || entry.target == null || entry.actual == null) {
            return { tone: "neutral", title: undefined };
        }
        const statusKey = PER_METRIC_STATUS_KEY[metricName];
        const status = tvaMatch.status && tvaMatch.status[statusKey];
        if (status !== "complete") return { tone: "neutral", title: undefined };
        const higher = METRIC_DIRECTION[metricName] === "higher";
        const met = higher ? Number(entry.actual) >= Number(entry.target)
                           : Number(entry.actual) <= Number(entry.target);
        const kind = METRIC_KIND[metricName];
        const dKind = diffKindFor(metricName);
        const t = cell(entry.target, kind);
        const a = cell(entry.actual, kind);
        const d = dKind === "pp" ? ppCell(entry.difference) : cell(entry.difference, dKind);
        return { tone: met ? "met" : "unmet",
                 title: i18n.t("reports.targetTitle", { defaultValue: "Target {{target}} · Actual {{actual}} · Δ {{diff}}", target: t, actual: a, diff: d }) };
    };
    return (
        <section className="kop-card p-5 mb-8" data-testid="corporate-sales-cost-analysis">
            {showKpis && (
            <>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Sales_Cost_Analysis", "Sales & Cost Analysis")}</h3>
                </div>
            </div>
            {/* Corporate · grouped KPI blocks (visual-only). Cards,
                values, tones, tooltips, bindings and testIDs are
                UNCHANGED — only the wrapping markup groups them so an
                owner can scan Sales → Purchases → Margin → Productivity
                in one go. Local spacing / divider styles are scoped to
                this section and never leak into shared components. */}
            <div className="space-y-5 mb-4" data-testid="scan-kpi-groups">
                <div data-testid="scan-group-sales">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{i18n.t("reports.l.Sales_customers", "Sales & customers")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        <KpiCell testid="scan-gross-sales" label={i18n.t("reports.l.Gross_Sales", "Gross Sales")} value={cell(kpi.gross_sales, "eur")} />
                        <KpiCell testid="scan-net-sales" label={i18n.t("reports.l.Net_Sales", "Net Sales")} value={cell(kpi.net_sales, "eur")}
                            {...monthlyTargetProps("net_sales")} />
                        <KpiCell testid="scan-customers" label={i18n.t("reports.l.Customers", "Customers")} value={cell(kpi.customers, "int")}
                            {...monthlyTargetProps("customers")} />
                        <KpiCell testid="scan-avg-spend" label={i18n.t("reports.l.Avg_Spend_Customer", "Avg Spend / Customer")} value={cell(kpi.average_spend_per_customer, "eur")} />
                    </div>
                </div>
                <div data-testid="scan-group-purchases">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{i18n.t("reports.l.Purchases_expenses", "Purchases & expenses")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        <KpiCell testid="scan-operational-purchases" label={i18n.t("reports.l.Operational_Purchases_NET", "Operational Purchases NET")} value={cell(kpi.purchases_net, "eur")}
                            sub={i18n.t("reports.l.exact_selected_from_to", "exact selected [from, to]")} />
                        <KpiCell testid="scan-delivery-fee" label={i18n.t("reports.l.Delivery_Fee_NET", "Delivery Fee NET")} value={cell(kpi.delivery_fee_net, "eur")}
                            sub={i18n.t("reports.l.separate_from_product_COGS_input", "separate from product COGS input")} />
                        <KpiCell testid="scan-total-expense" label={i18n.t("reports.l.Total_Expense_NET", "Total Expense NET")} value={cell(kpi.total_expense_net, "eur")}
                            sub={i18n.t("reports.l.products_delivery_included_once", "products + delivery · included once")} />
                        <KpiCell testid="scan-waste" label={i18n.t("reports.l.Waste_NET", "Waste NET")} value={cell(kpi.waste_net, "eur")} />
                    </div>
                </div>
                <div data-testid="scan-group-margin">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{i18n.t("reports.l.Food_cost_margin", "Food cost & margin")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        <KpiCell testid="scan-actual-cogs" label={i18n.t("reports.l.Actual_COGS", "Actual COGS")}
                            value={cell(kpi.period_actual_cogs, "eur")}
                            sub={i18n.t("reports.l.COGS_Purchases_full_calendar_month_s", "COGS Purchases · full calendar month(s)")} />
                        <KpiCell testid="scan-food-cost" label={i18n.t("reports.l.Actual_Food_Cost_PCT", "Actual Food Cost %")} value={cell(kpi.period_food_cost_pct, "pct")}
                            {...monthlyTargetProps("food_cost_pct")} />
                        <KpiCell testid="scan-kate-eur" label={i18n.t("reports.l.Kate_EUR", "Kate €")} value={cell(kpi.period_kate_eur, "eur")} />
                        <KpiCell testid="scan-kate-pct" label={i18n.t("reports.l.Kate_PCT", "Kate %")} value={cell(kpi.period_kate_pct, "pct")}
                            {...monthlyTargetProps("kate_pct")} />
                    </div>
                </div>
                <div data-testid="scan-group-productivity">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-neutral-500 mb-2">{i18n.t("reports.l.Hours_productivity", "Hours & productivity")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        <KpiCell testid="scan-worked-hours" label={i18n.t("reports.l.Worked_Hours", "Worked Hours")}
                            value={kpi.worked_hours == null ? "—" : `${Number(kpi.worked_hours).toFixed(1)} h`}
                            sub={hoursSourceSub(kpi)} />
                        <KpiCell testid="scan-teho" label={i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)")}
                            value={cell(kpi.teho, "eur")}
                            sub={tehoSub(kpi)} />
                        <HoursSplitCells kpi={kpi} prefix="scan" />
                    </div>
                    <HoursCoverageNote kpi={kpi} prefix="scan" />
                </div>
            </div>
            <details className="rounded-xl border border-neutral-200/70 bg-neutral-50/60 px-4 py-2 group"
                     data-testid="scan-how-calculated">
                <summary className="cursor-pointer list-none flex items-center justify-between text-[12px] font-semibold text-neutral-700 py-1 select-none">
                    <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true" className="text-neutral-400 group-open:rotate-90 transition-transform inline-block">▸</span>
                        {i18n.t("reports.l.How_it_is_calculated", "How it is calculated")}
                    </span>
                </summary>
                <p className="mt-2 text-[12px] leading-relaxed text-neutral-600">
                    {i18n.t("reports.l.Selected_range_totals_Operational_Purchases_use_the_exact_from_to_wind", "Selected-range totals. Operational Purchases use the exact [from, to] window; COGS Purchases use the full accounting calendar month and appear only where a valid Physical Inventory boundary exists.")}
                </p>
            </details>
            </>
            )}
            {showPeriod && (
            <details className="group mt-1" data-testid="scan-period-collapsible">
                <summary className="cursor-pointer list-none select-none inline-flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-neutral-500 mb-2 px-1 py-1 rounded hover:bg-neutral-50"
                         data-testid="scan-period-toggle">
                    <span aria-hidden="true" className="inline-block text-neutral-400 transition-transform group-open:rotate-90">▸</span>
                    <span>{i18n.t("reports.l.Period_breakdown", { defaultValue: "Period breakdown · {{g}}", g: groupingLabel })}</span>
                </summary>
                <div className="kop-card ms-rt-wrap">
                {grouping === "daily" ? (
                <table className="w-full text-sm ms-rt" data-testid="scan-breakdown">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-3 py-2 font-semibold whitespace-nowrap">{i18n.t("reports.col.day", "Day")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Gross_Sales", "Gross Sales")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Net_Sales", "Net Sales")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Purchases_NET", "Purchases NET")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap"
                                title={i18n.t("reports.l.Purchase_based_margin_Net_Sales_minus_Purchases_NET_Based_on_purchases", "Purchase-based margin: Net Sales − Purchases NET. Based on purchases recorded that day; distinct from COGS-based Kate.")}>
                                {i18n.t("reports.l.Margin_EUR", "Margin €")}
                            </th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap"
                                title={i18n.t("reports.l.Purchase_based_margin_Margin_EUR_Net_Sales_100_Distinct_from_COGS_base", "Purchase-based margin: Margin € / Net Sales × 100. Distinct from COGS-based Kate.")}>
                                {i18n.t("reports.l.Margin_PCT", "Margin %")}
                            </th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Waste_NET", "Waste NET")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Hours", "Hours")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Customers", "Customers")}</th>
                            <th className="px-3 py-2 font-semibold text-right whitespace-nowrap">{i18n.t("reports.l.Avg_Spend", "Avg Spend")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-3 py-3 text-sm text-neutral-500"
                                    data-testid="scan-breakdown-empty" colSpan={11}>
                                    {i18n.t("reports.l.No_periods_in_the_selected_range_have_data", "No periods in the selected range have data.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.bucket_key} data-testid={`scan-row-${p.bucket_key}`}>
                                <td className="px-3 py-2 font-medium whitespace-nowrap">{fmtDayCompact(p.bucket_key)}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.gross_sales, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.net_sales, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.purchases_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`scan-row-${p.bucket_key}-purchase-margin-eur`}
                                    title={i18n.t("reports.l.Purchase_based_margin_Net_Sales_minus_Purchases_NET", "Purchase-based margin: Net Sales − Purchases NET.")}>
                                    {cell(purchaseMarginEur(p), "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`scan-row-${p.bucket_key}-purchase-margin-pct`}
                                    title={i18n.t("reports.l.Purchase_based_margin_Margin_EUR_Net_Sales_100", "Purchase-based margin: Margin € / Net Sales × 100.")}>
                                    {cell(purchaseMarginPct(p), "pct")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.waste_net, "eur")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`scan-row-${p.bucket_key}-worked-hours`}>
                                    {hoursCell(p.worked_hours)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid={`scan-row-${p.bucket_key}-teho`}>
                                    {cell(p.teho, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.customers, "int")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{cell(p.average_spend_per_customer, "eur")}</td>
                            </tr>
                        ))}
                        {rows.length > 0 && (
                            <tr data-testid="scan-breakdown-total"
                                className="bg-neutral-50 font-semibold border-t-2 border-neutral-200">
                                <td className="px-3 py-2 whitespace-nowrap">{i18n.t("reports.total", "Total")}</td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-gross-sales">
                                    {cell(dailyTotals.totGross, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-net-sales">
                                    {cell(dailyTotals.totNet, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-purchases-net">
                                    {cell(dailyTotals.totPurch, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-purchase-margin-eur"
                                    title={i18n.t("reports.l.Purchase_based_margin_Total_Net_Sales_minus_Total_Purchases_NET", "Purchase-based margin: Total Net Sales − Total Purchases NET.")}>
                                    {cell(dailyTotals.totMarginEur, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-purchase-margin-pct"
                                    title={i18n.t("reports.l.Total_Margin_EUR_Total_Net_Sales_100_Daily_percentages_are_never_avera", "Total Margin € / Total Net Sales × 100. Daily percentages are never averaged.")}>
                                    {cell(dailyTotals.totMarginPct, "pct")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-waste-net">
                                    {cell(dailyTotals.totWaste, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-worked-hours">
                                    {hoursCell(dailyTotals.totHours)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-teho"
                                    title={i18n.t("reports.l.Total_Net_Sales_div_Total_Worked_Hours_Daily_Teho_is_never_averaged", "Total Net Sales ÷ Total Worked Hours. Daily Teho is never averaged.")}>
                                    {cell(dailyTotals.totTeho, "eur")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-customers">
                                    {cell(dailyTotals.totCust, "int")}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"
                                    data-testid="scan-breakdown-total-avg-spend"
                                    title={i18n.t("reports.l.Total_Net_Sales_div_Total_Customers_Daily_Avg_Spend_is_never_averaged", "Total Net Sales ÷ Total Customers. Daily Avg Spend is never averaged.")}>
                                    {cell(dailyTotals.totAvgSpend, "eur")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                ) : (
                <table className="w-full text-sm ms-rt" data-testid="scan-breakdown">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.col.period", "Period")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Gross_Sales", "Gross Sales")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Net_Sales", "Net Sales")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Purchases_NET", "Purchases NET")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Waste_NET", "Waste NET")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Customers", "Customers")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Avg_Spend", "Avg Spend")}</th>
                            {showAccountingCols && (
                                <>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Opening_Inv", "Opening Inv.")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.COGS_Purch", "COGS Purch.")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Closing_Inv", "Closing Inv.")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Actual_COGS", "Actual COGS")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Food_Cost_PCT", "Food Cost %")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Kate_EUR", "Kate €")}</th>
                                    <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Kate_PCT", "Kate %")}</th>
                                </>
                            )}
                            <th className="px-5 py-3 font-semibold text-right"
                                title={i18n.t("reports.l.Based_on_purchases_recorded_that_day_products_may_be_consumed_later", "Based on purchases recorded that day; products may be consumed later.")}>
                                {i18n.t("reports.l.Purchase_based_Margin_EUR", "Purchase-based Margin €")}
                            </th>
                            <th className="px-5 py-3 font-semibold text-right"
                                title={i18n.t("reports.l.Based_on_purchases_recorded_that_day_products_may_be_consumed_later", "Based on purchases recorded that day; products may be consumed later.")}>
                                {i18n.t("reports.l.Purchase_based_Margin_PCT", "Purchase-based Margin %")}
                            </th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Worked_Hours", "Worked Hours")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-5 py-4 text-sm text-neutral-500"
                                    data-testid="scan-breakdown-empty" colSpan={showAccountingCols ? 18 : 11}>
                                    {i18n.t("reports.l.No_periods_in_the_selected_range_have_data", "No periods in the selected range have data.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.bucket_key} data-testid={`scan-row-${p.bucket_key}`}>
                                <td className="px-5 py-3 font-medium">{p.bucket_key}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.gross_sales, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.net_sales, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.purchases_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.waste_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.customers, "int")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.average_spend_per_customer, "eur")}</td>
                                {showAccountingCols && (
                                    <>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.opening_inventory_net_sum, "eur")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.cogs_purchases_valid_sum, "eur")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.closing_inventory_net_sum, "eur")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.actual_cogs, "eur")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.food_cost_pct, "pct")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.kate_eur, "eur")}</td>
                                        <td className="px-5 py-3 text-right tabular-nums">{cell(p.kate_pct, "pct")}</td>
                                    </>
                                )}
                                <td className="px-5 py-3 text-right tabular-nums"
                                    data-testid={`scan-row-${p.bucket_key}-purchase-margin-eur`}
                                    title={i18n.t("reports.l.Based_on_purchases_recorded_that_day_products_may_be_consumed_later", "Based on purchases recorded that day; products may be consumed later.")}>
                                    {cell(purchaseMarginEur(p), "eur")}
                                </td>
                                <td className="px-5 py-3 text-right tabular-nums"
                                    data-testid={`scan-row-${p.bucket_key}-purchase-margin-pct`}
                                    title={i18n.t("reports.l.Based_on_purchases_recorded_that_day_products_may_be_consumed_later", "Based on purchases recorded that day; products may be consumed later.")}>
                                    {cell(purchaseMarginPct(p), "pct")}
                                </td>
                                <td className="px-5 py-3 text-right tabular-nums"
                                    data-testid={`scan-row-${p.bucket_key}-worked-hours`}>
                                    {hoursCell(p.worked_hours)}
                                </td>
                                <td className="px-5 py-3 text-right tabular-nums"
                                    data-testid={`scan-row-${p.bucket_key}-teho`}>
                                    {cell(p.teho, "eur")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                )}
                </div>
            </details>
            )}
        </section>
    );
}

const BOUNDARY_LABEL = () => ({
    valid: i18n.t("reports.l.Valid", "Valid"),
    missing_opening: i18n.t("reports.l.Missing_Opening", "Missing Opening"),
    missing_closing: i18n.t("reports.l.Missing_Closing", "Missing Closing"),
    missing_both: i18n.t("reports.l.Missing_Both", "Missing Both"),
    partial_selected_period: i18n.t("reports.l.Partial_Selected_Period", "Partial Selected Period"),
});

function InventoryCogsAnalysis({ dash }) {
    // Block 4 · per-restaurant × per-inventory-month accounting drilldown.
    // Every row exposes Opening + COGS Purchases − Closing = Actual COGS so
    // the arithmetic is auditable. No fabricated values: partial months and
    // missing-boundary months render "—" for their accounting fields.
    const rows = dash?.inventory_cogs?.by_restaurant_month || [];
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : `€ ${FMT_NUM(v)}`);
    return (
        <section className="kop-card p-5 mb-8" data-testid="corporate-inventory-cogs">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Inventory_COGS", "Inventory / COGS")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.l.Per_restaurant_inventory_month_Actual_COGS_Opening_COGS_Purchases_minu", "Per-restaurant × inventory month. Actual COGS = Opening + COGS Purchases − Closing. Missing-boundary rows keep null accounting fields — never fabricated.")}
                    </p>
                </div>
            </div>
            <div className="kop-card ms-rt-wrap">
                <table className="w-full text-sm ms-rt" data-testid="inv-cogs-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.l.Restaurant", "Restaurant")}</th>
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.col.month", "Month")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Opening_Inv", "Opening Inv.")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.COGS_Purch", "COGS Purch.")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Closing_Inv", "Closing Inv.")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Actual_COGS", "Actual COGS")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Net_Sales_valid_basis", "Net Sales (valid basis)")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Food_Cost_PCT", "Food Cost %")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Kate_EUR", "Kate €")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Kate_PCT", "Kate %")}</th>
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.l.Boundary_Status", "Boundary Status")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-5 py-4 text-sm text-neutral-500"
                                    data-testid="inv-cogs-empty" colSpan={11}>
                                    {i18n.t("reports.l.No_inventory_months_touch_the_selected_window", "No inventory months touch the selected window.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => (
                            <tr key={`${r.restaurant_id}-${r.month}`}
                                data-testid={`inv-cogs-row-${r.restaurant_id}-${r.month}`}>
                                <td className="px-5 py-3 font-medium">{r.restaurant_name}</td>
                                <td className="px-5 py-3">{r.month}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.opening_inventory_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.cogs_purchases_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.closing_inventory_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.actual_cogs, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.cogs_net_sales, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.food_cost_pct, "pct")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.kate_eur, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(r.kate_pct, "pct")}</td>
                                <td className="px-5 py-3" data-testid={`inv-cogs-status-${r.restaurant_id}-${r.month}`}>
                                    {BOUNDARY_LABEL()[r.boundary_status] || r.boundary_status}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function SupplierAnalysis({ dash }) {
    // Block 5 · READ-ONLY supplier spend section. Consumes the shared
    // `/api/corporate/dashboard` payload driven by the central Corporate
    // filter state (restaurant / from / to / grouping / comparison).
    // NEVER performs authoritative financial math in JavaScript — all
    // percentages / averages come pre-computed from the backend.
    const sup = dash?.suppliers || null;
    const bySupplier = sup?.by_supplier;
    const rows = useMemo(() => bySupplier || [], [bySupplier]);
    const compRows = sup?.comparison?.by_supplier || [];
    const compBySid = {};
    for (const r of compRows) {
        compBySid[r.supplier_id || "__unknown__"] = r.purchases_net;
    }
    const [sortKey, setSortKey] = useState("purchases_net");
    const [sortDir, setSortDir] = useState("desc");
    const sortedRows = useMemo(() => {
        const rs = [...rows];
        rs.sort((a, b) => {
            const av = a[sortKey]; const bv = b[sortKey];
            const cmp = av == null ? 1 : bv == null ? -1
                : (typeof av === "number" ? av - bv
                    : String(av).localeCompare(String(bv)));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return rs;
    }, [rows, sortKey, sortDir]);
    const toggle = (k) => {
        if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(k); setSortDir(k === "supplier_name" ? "asc" : "desc"); }
    };
    const th = (k, label, right) => (
        <th key={k}
            className={`px-5 py-3 font-semibold cursor-pointer select-none ${right ? "text-right" : ""}`}
            data-testid={`sup-th-${k}`}
            onClick={() => toggle(k)}>
            {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </th>
    );
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : kind === "int" ? Number(v).toLocaleString("en-US")
        : `€ ${FMT_NUM(v)}`);
    return (
        <section className="kop-card p-5 mb-8" data-testid="corporate-supplier-analysis">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Supplier_Analysis", "Supplier Analysis")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.l.Completed_deliveries_only_Shares_are_computed_from_company_totals_neve", "Completed deliveries only. Shares are computed from company totals, never averaged. Sort is a user inspection aid.")}
                    </p>
                </div>
                <div className="text-right text-[11px] text-neutral-500 tabular-nums">
                    <div data-testid="sup-total-net">
                        {i18n.t("reports.l.Total_Purchases_NET", "Total Purchases NET:")} {cell(sup?.total_purchases_net, "eur")}
                    </div>
                    <div data-testid="sup-total-count">
                        {i18n.t("reports.l.Total_Delivery_Count", "Total Delivery Count:")} {cell(sup?.total_delivery_count, "int")}
                    </div>
                </div>
            </div>
            {sup?.comparison?.spec_conflict && (
                <div className="mb-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                    data-testid="sup-spec-conflict">
                    SPEC CONFLICT / CLARIFICATION REQUIRED — {sup.comparison.spec_conflict}
                </div>
            )}
            <div className="kop-card ms-rt-wrap">
                <table className="w-full text-sm ms-rt" data-testid="sup-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            {th("supplier_name", i18n.t("reports.l.Supplier", "Supplier"), false)}
                            {th("purchases_net", i18n.t("reports.l.Purchases_NET", "Purchases NET"), true)}
                            {th("pct_of_total", i18n.t("reports.l.PCT_of_Total", "% of Total"), true)}
                            {th("delivery_count", i18n.t("reports.l.Delivery_Count", "Delivery Count"), true)}
                            {th("average_delivery_net", i18n.t("reports.l.Avg_Delivery_NET", "Avg Delivery NET"), true)}
                            {sup?.comparison && (
                                <>
                                    <th className="px-5 py-3 font-semibold text-right" data-testid="sup-th-prev">{i18n.t("reports.l.Prev_NET", "Prev NET")}</th>
                                    <th className="px-5 py-3 font-semibold text-right" data-testid="sup-th-diff">{i18n.t("reports.l.Delta_NET", "Δ NET")}</th>
                                    <th className="px-5 py-3 font-semibold text-right" data-testid="sup-th-pct-change">{i18n.t("reports.l.PCT_Change", "% Change")}</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {sortedRows.length === 0 && (
                            <tr>
                                <td className="px-5 py-4 text-sm text-neutral-500"
                                    data-testid="sup-table-empty" colSpan={sup?.comparison ? 8 : 5}>
                                    {i18n.t("reports.l.No_completed_deliveries_in_the_selected_window", "No completed deliveries in the selected window.")}
                                </td>
                            </tr>
                        )}
                        {sortedRows.map((r) => {
                            const key = r.supplier_id || "__unknown__";
                            const c = compBySid[key];
                            return (
                                <tr key={key} data-testid={`sup-row-${key}`}>
                                    <td className="px-5 py-3 font-medium">{r.supplier_name}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{cell(r.purchases_net, "eur")}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{cell(r.pct_of_total, "pct")}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{cell(r.delivery_count, "int")}</td>
                                    <td className="px-5 py-3 text-right tabular-nums">{cell(r.average_delivery_net, "eur")}</td>
                                    {sup?.comparison && (
                                        <>
                                            <td className="px-5 py-3 text-right tabular-nums">{cell(c?.previous, "eur")}</td>
                                            <td className="px-5 py-3 text-right tabular-nums">{cell(c?.diff, "eur")}</td>
                                            <td className="px-5 py-3 text-right tabular-nums">{cell(c?.pct_change, "pct")}</td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

// --------------------------------------------------------------------
// Labour section (Corporate) — strictly Core Daily Performance data.
// This is NOT a staff-scheduling integration. Only the two Core metrics
// that already exist in the Corporate KPI payload are surfaced: Worked
// Hours and Teho. Other labour-cost concepts belong to a future
// dedicated module and MUST NOT appear here.
// --------------------------------------------------------------------
function LabourAnalysis({ dash }) {
    const kpi = dash?.kpi || {};
    const cell = (v, kind) => (v == null ? "—"
        : kind === "hours" ? `${Number(v).toFixed(1)} h`
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : `€ ${FMT_NUM(v)}`);
    const rows = dash?.trend || [];
    const grouping = dash?.filters?.grouping || "daily";
    return (
        <section className="kop-card p-5" data-testid="corporate-labour-analysis">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Labour", "Labour")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.l.Daily_Performance_hours_manual_entry_or_confirmed_employee_actuals_app", "Daily Performance hours: manual entry, or confirmed employee actuals + approved external extras from the automatic start date.")}
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <KpiCell testid="labour-worked-hours" label={i18n.t("reports.l.Worked_Hours", "Worked Hours")}
                    value={cell(kpi.worked_hours, "hours")} sub={hoursSourceSub(kpi)} />
                <HoursSplitCells kpi={kpi} prefix="labour" />
                <KpiCell testid="labour-teho" label={i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)")}
                    value={cell(kpi.teho, "eur")}
                    sub={tehoSub(kpi)} />
                <KpiCell testid="labour-net-sales" label={i18n.t("reports.l.Net_Sales", "Net Sales")}
                    value={cell(kpi.net_sales, "eur")} />
                <KpiCell testid="labour-customers" label={i18n.t("reports.l.Customers", "Customers")}
                    value={kpi.customers == null ? "—" : Number(kpi.customers).toLocaleString("en-US")} />
            </div>
            <div className="mb-4"><HoursCoverageNote kpi={kpi} prefix="labour" /></div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-neutral-500 mb-2">
                {i18n.t("reports.l.Period_breakdown", { defaultValue: "Period breakdown · {{g}}", g: i18n.t(`reports.grouping.${grouping}`, grouping) })}
            </div>
            <div className="kop-card ms-rt-wrap">
                <table className="w-full text-sm ms-rt" data-testid="labour-breakdown">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.col.period", "Period")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Net_Sales", "Net Sales")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Worked_Hours", "Worked Hours")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Teho_EUR_h", "Teho (€/h)")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-5 py-4 text-sm text-neutral-500"
                                    data-testid="labour-breakdown-empty" colSpan={4}>
                                    {i18n.t("reports.l.No_periods_in_the_selected_range_have_data", "No periods in the selected range have data.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.bucket_key} data-testid={`labour-row-${p.bucket_key}`}>
                                <td className="px-5 py-3 font-medium">{p.bucket_key}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.net_sales, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.worked_hours, "hours")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.teho, "eur")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

// --------------------------------------------------------------------
// Waste section (Corporate) — canonical waste data already returned by
// the Corporate calculation engine. Reuses Waste € and Waste % of
// Purchases from the KPI + trend payload. NEVER adds Waste % Sales /
// Waste % COGS. NEVER alters production_reviews or waste formulas.
// --------------------------------------------------------------------
function WasteAnalysis({ dash }) {
    const kpi = dash?.kpi || {};
    const rows = dash?.trend || [];
    const grouping = dash?.filters?.grouping || "daily";
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : `€ ${FMT_NUM(v)}`);
    return (
        <section className="kop-card p-5" data-testid="corporate-waste-analysis">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Waste", "Waste")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.l.Confirmed_production_reviews_Waste_PCT_Purchases_uses_the_exact_canoni", "Confirmed production reviews. Waste % Purchases uses the exact canonical formula already applied in Corporate.")}
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <KpiCell testid="waste-net" label={i18n.t("reports.l.Waste_EUR", "Waste €")}
                    value={cell(kpi.waste_net, "eur")} />
                <KpiCell testid="waste-pct-purchases" label={i18n.t("reports.l.Waste_PCT_of_Purchases", "Waste % of Purchases")}
                    value={cell(kpi.waste_pct_of_purchases, "pct")} />
                <KpiCell testid="waste-purchases-net" label={i18n.t("reports.l.Purchases_NET", "Purchases NET")}
                    value={cell(kpi.purchases_net, "eur")} />
            </div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-neutral-500 mb-2">
                {i18n.t("reports.l.Period_breakdown", { defaultValue: "Period breakdown · {{g}}", g: i18n.t(`reports.grouping.${grouping}`, grouping) })}
            </div>
            <div className="kop-card ms-rt-wrap">
                <table className="w-full text-sm ms-rt" data-testid="waste-breakdown">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-5 py-3 font-semibold">{i18n.t("reports.col.period", "Period")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Waste_EUR", "Waste €")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Purchases_NET", "Purchases NET")}</th>
                            <th className="px-5 py-3 font-semibold text-right">{i18n.t("reports.l.Waste_PCT_Purch", "Waste % Purch.")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-5 py-4 text-sm text-neutral-500"
                                    data-testid="waste-breakdown-empty" colSpan={4}>
                                    {i18n.t("reports.l.No_periods_in_the_selected_range_have_data", "No periods in the selected range have data.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.bucket_key} data-testid={`waste-row-${p.bucket_key}`}>
                                <td className="px-5 py-3 font-medium">{p.bucket_key}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.waste_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.purchases_net, "eur")}</td>
                                <td className="px-5 py-3 text-right tabular-nums">{cell(p.waste_pct_of_purchases, "pct")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

// Block 6 · Target management + Target vs Actual (Corporate).
// Both components consume the shared central Corporate filter state
// via the parent `CorporateReport`. Actuals in `dash.target_vs_actual`
// come from the authoritative backend calculation — NEVER computed in
// JavaScript, and NEVER prorated for partial months / partial daily
// coverage. Unavailable values render "—".

const TARGET_STATUS_LABEL = () => ({
    complete: i18n.t("reports.l.Complete", "Complete"),
    partial_selected_period: i18n.t("reports.l.Partial_Selected_Period", "Partial Selected Period"),
    partial_daily_coverage: i18n.t("reports.l.Partial_Daily_Coverage", "Partial Daily Coverage"),
    missing_cogs_boundary: i18n.t("reports.l.Missing_COGS_Boundary", "Missing COGS Boundary"),
    missing_actual: i18n.t("reports.l.Missing_Actual", "Missing Actual"),
    no_target: i18n.t("reports.l.No_Target", "No Target"),
});

function TargetsPanel({ restaurants }) {
    const { user } = useApp();
    const isCorporate = user?.role === "corporate";
    const [rid, setRid] = useState(
        isCorporate ? (restaurants[0]?.id || "") : (user?.restaurant_id || ""));
    // The parent fetches `restaurants` asynchronously, so the `useState`
    // initializer above runs once at mount with `restaurants === []`,
    // leaving `rid=""` for the corporate role. The <select> then renders
    // the first option visually (HTML default when the controlled value
    // matches no <option>), while state stays empty — so `Create Target`
    // POSTed restaurant_id="" and the backend 400'd with
    // "restaurant_id required". Sync state to the visible first option
    // once the list arrives, without touching an existing user choice.
    useEffect(() => {
        if (!rid && restaurants && restaurants.length) {
            setRid(restaurants[0].id);
        }
    }, [restaurants, rid]);
    const [month, setMonth] = useState(() => todayISO().slice(0, 7));
    const [existing, setExisting] = useState(null);
    const [fields, setFields] = useState({
        net_sales_target: "", food_cost_pct_target: "",
        kate_pct_target: "", waste_pct_target: "",
        customers_target: "",
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const [ok, setOk] = useState(null);
    const clean = () => { setErr(null); setOk(null); };

    useEffect(() => {
        clean();
        if (!rid || !month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
            setExisting(null); return;
        }
        api.get("/corporate/targets", { params: {
            restaurant_id: rid, from_month: month, to_month: month,
        }}).then(({ data }) => {
            const hit = (data || [])[0] || null;
            setExisting(hit);
            setFields({
                net_sales_target: hit?.net_sales_target ?? "",
                food_cost_pct_target: hit?.food_cost_pct_target ?? "",
                kate_pct_target: hit?.kate_pct_target ?? "",
                waste_pct_target: hit?.waste_pct_target ?? "",
                customers_target: hit?.customers_target ?? "",
            });
        }).catch(() => setExisting(null));
    }, [rid, month]);

    const _parse = (v, integer) => {
        if (v === "" || v == null) return null;
        const n = Number(v);
        if (Number.isNaN(n)) return v;
        if (integer && !Number.isInteger(n)) return v;
        return integer ? n : n;
    };

    const save = async () => {
        clean(); setSaving(true);
        try {
            const body = {
                net_sales_target: _parse(fields.net_sales_target, false),
                food_cost_pct_target: _parse(fields.food_cost_pct_target, false),
                kate_pct_target: _parse(fields.kate_pct_target, false),
                waste_pct_target: _parse(fields.waste_pct_target, false),
                customers_target: _parse(fields.customers_target, true),
            };
            if (existing?.id) {
                const { data } = await api.patch(
                    `/corporate/targets/${existing.id}`, body);
                setExisting(data);
                setOk(i18n.t("reports.l.Target_updated", "Target updated."));
            } else {
                const { data } = await api.post("/corporate/targets", {
                    restaurant_id: rid, target_month: month, ...body,
                });
                setExisting(data);
                setOk(i18n.t("reports.l.Target_created", "Target created."));
            }
        } catch (e) {
            setErr(detailOr(e?.response?.data?.detail, i18n.t("reports.l.Failed_to_save", "Failed to save")));
        } finally {
            setSaving(false);
        }
    };

    const upd = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
    return (
        <section className="kop-card p-5 mb-8" data-testid="corporate-targets-panel">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Targets", "Targets")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.l.Monthly_per_restaurant_A_monthly_target_stays_as_a_whole_month_value_i", "Monthly, per restaurant. A monthly target stays as a whole-month value in every partial window — it is not scaled down or split across days.")}
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 mb-3 items-end">
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Restaurant", "Restaurant")}</label>
                    <select data-testid="tgt-restaurant" value={rid}
                        onChange={(e) => setRid(e.target.value)}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm bg-white">
                        {restaurants.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Target_Month", "Target Month")}</label>
                    <input type="month" data-testid="tgt-month" value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm bg-white" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Net_Sales_EUR", "Net Sales €")}</label>
                    <input type="number" min="0" step="0.01"
                        data-testid="tgt-net-sales" value={fields.net_sales_target}
                        onChange={upd("net_sales_target")}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Food_Cost_PCT", "Food Cost %")}</label>
                    <input type="number" min="0" step="0.1"
                        data-testid="tgt-food-cost" value={fields.food_cost_pct_target}
                        onChange={upd("food_cost_pct_target")}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Kate_PCT", "Kate %")}</label>
                    <input type="number" min="0" step="0.1"
                        data-testid="tgt-kate" value={fields.kate_pct_target}
                        onChange={upd("kate_pct_target")}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Waste_PCT", "Waste %")}</label>
                    <input type="number" min="0" step="0.1"
                        data-testid="tgt-waste" value={fields.waste_pct_target}
                        onChange={upd("waste_pct_target")}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{i18n.t("reports.l.Customers", "Customers")}</label>
                    <input type="number" min="0" step="1"
                        data-testid="tgt-customers" value={fields.customers_target}
                        onChange={upd("customers_target")}
                        className="h-10 w-full px-2 border border-neutral-300 rounded-md text-sm" />
                </div>
            </div>
            <div className="flex items-center gap-3">
                <button type="button" onClick={save} disabled={saving}
                    data-testid="tgt-save-btn"
                    className="h-9 px-4 rounded-md bg-neutral-900 text-white text-sm">
                    {existing ? i18n.t("reports.l.Update_Target", "Update Target") : i18n.t("reports.l.Create_Target", "Create Target")}
                </button>
                <span className="text-[11px] text-neutral-500" data-testid="tgt-existing">
                    {existing ? `Editing existing target · id ${existing.id.slice(0, 8)}…` : i18n.t("reports.l.No_target_yet_for_this_restaurant_month", "No target yet for this restaurant + month.")}
                </span>
                {ok && <span className="text-[11px] text-emerald-700" data-testid="tgt-ok">{ok}</span>}
                {err && <span className="text-[11px] text-rose-700" data-testid="tgt-err">{String(err)}</span>}
            </div>
        </section>
    );
}

function TargetVsActual({ dash }) {
    const rows = dash?.target_vs_actual?.by_restaurant_month || [];
    const cell = (v, kind) => (v == null ? "—"
        : kind === "pct" ? `${Number(v).toFixed(1)}%`
        : kind === "pp" ? `${Number(v).toFixed(1)} pp`
        : kind === "int" ? Number(v).toLocaleString("en-US")
        : `€ ${FMT_NUM(v)}`);
    const statusPill = (s) => {
        const label = TARGET_STATUS_LABEL()[s] || s;
        const tone = s === "complete"
            ? "bg-neutral-100 text-neutral-700 border-neutral-200"
            : "bg-amber-50 text-amber-800 border-amber-200";
        return (
            <span className={`inline-block px-2 py-0.5 rounded border text-[10px] ${tone}`}
                data-testid={`tva-status-${s}`}>{label}</span>
        );
    };

    // Monthly-only target status. The eligibility rule reuses the
    // backend-provided per-metric status (`complete` means the exact
    // calendar month, same restaurant and full data coverage are
    // honoured). Daily / weekly rows never enter this helper.
    //   direction = "higher"  → actual ≥ target meets the target
    //                           (Net Sales, Customers, Kate %)
    //   direction = "lower"   → actual ≤ target meets the target
    //                           (Food Cost %, Waste %)
    // Equality = met. Anything else (no target, partial month,
    // incomplete daily coverage, missing COGS boundary, missing
    // actual) → neutral and no colour is applied.
    const metricTone = (entry, direction, perMetricStatus, hasTarget) => {
        if (!hasTarget) return "neutral";
        if (perMetricStatus !== "complete") return "neutral";
        if (entry == null || entry.target == null || entry.actual == null) return "neutral";
        const a = Number(entry.actual);
        const t = Number(entry.target);
        const met = direction === "higher" ? a >= t : a <= t;
        return met ? "met" : "unmet";
    };
    const DOT_TONE = {
        met:     "bg-emerald-500",
        unmet:   "bg-rose-500",
        neutral: "bg-neutral-300",
    };
    const TEXT_TONE = {
        met:     "text-emerald-700 font-medium",
        unmet:   "text-rose-700 font-medium",
        neutral: "",
    };
    const TONE_LABEL = {
        met: i18n.t("reports.l.Target_met", "Target met"),
        unmet: i18n.t("reports.l.Target_not_met", "Target not met"),
        neutral: i18n.t("reports.l.No_valid_monthly_comparison", "No valid monthly comparison"),
    };
    // Compact month label e.g. "Sep 2026" without changing stored
    // dates. Falls back to the raw YYYY-MM string if parsing fails.
    const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun",
                           "Jul","Aug","Sep","Oct","Nov","Dec"];
    const compactMonth = (ym) => {
        if (!ym || typeof ym !== "string" || ym.length < 7) return ym || "—";
        const y = Number(ym.slice(0, 4));
        const m = Number(ym.slice(5, 7));
        if (!y || !m || m < 1 || m > 12) return ym;
        return `${i18n.t(`monthsShort.${m}`, MONTH_SHORT[m - 1])} ${y}`;
    };
    const metricCell = (entry, kind, direction, perMetricStatus, hasTarget, testid) => {
        // Simplified presentation: ACTUAL only in the cell; the raw
        // Target / Actual / Δ remain fully available via the hover /
        // focus tooltip. "—" is preserved for missing values.
        const tone = metricTone(entry, direction, perMetricStatus, hasTarget);
        const diffKind = kind === "pct" ? "pp" : kind;
        const tTxt = cell(entry?.target, kind);
        const aTxt = cell(entry?.actual, kind);
        const dTxt = cell(entry?.difference, diffKind);
        const title = i18n.t("reports.targetTitle", { defaultValue: "Target {{target}} · Actual {{actual}} · Δ {{diff}}", target: tTxt, actual: aTxt, diff: dTxt });
        const detailId = `${testid}-detail`;
        return (
            <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap"
                title={title} data-testid={testid}>
                <span className="group relative inline-flex items-center justify-end gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/15 rounded"
                      tabIndex={0}
                      aria-describedby={detailId}>
                    <span aria-hidden="true"
                        className={`inline-block w-2 h-2 rounded-full ${DOT_TONE[tone]}`}
                        data-testid={`${testid}-dot-${tone}`} />
                    <span className="sr-only">{TONE_LABEL[tone]}. {title}</span>
                    <span className={TEXT_TONE[tone]}>{aTxt}</span>
                    <span id={detailId}
                          role="tooltip"
                          data-testid={detailId}
                          className="pointer-events-none absolute right-0 top-full mt-1 z-10 opacity-0 group-focus:opacity-100 group-focus-within:opacity-100 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] text-neutral-700 shadow-md tabular-nums whitespace-nowrap">
                        {title}
                    </span>
                </span>
            </td>
        );
    };
    return (
        <section className="kop-card p-5 mb-8" data-testid="corporate-target-vs-actual">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h3 className="font-display text-xl font-bold">{i18n.t("reports.l.Target_vs_Actual", "Target vs Actual")}</h3>
                    <p className="text-[11px] text-neutral-500">
                        {i18n.t("reports.tvaNote", "Monthly rows. Each cell shows the actual result; the underlying target and difference remain available on hover. Partial periods and missing coverage surface as factual status labels.")}
                    </p>
                </div>
            </div>
            <div className="kop-card ms-rt-wrap">
                <table className="min-w-full text-sm ms-rt" data-testid="tva-table">
                    <thead className="bg-neutral-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                            <th className="px-4 py-3 font-semibold">{i18n.t("reports.l.Restaurant", "Restaurant")}</th>
                            <th className="px-4 py-3 font-semibold">{i18n.t("reports.col.month", "Month")}</th>
                            <th className="px-4 py-3 font-semibold text-right">{i18n.t("reports.l.Net_Sales", "Net Sales")}</th>
                            <th className="px-4 py-3 font-semibold text-right">{i18n.t("reports.l.Food_Cost_PCT", "Food Cost %")}</th>
                            <th className="px-4 py-3 font-semibold text-right">{i18n.t("reports.l.Kate_PCT", "Kate %")}</th>
                            <th className="px-4 py-3 font-semibold text-right">{i18n.t("reports.l.Waste_PCT", "Waste %")}</th>
                            <th className="px-4 py-3 font-semibold text-right">{i18n.t("reports.l.Customers", "Customers")}</th>
                            <th className="px-4 py-3 font-semibold">{i18n.t("reports.col.status", "Status")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-4 py-4 text-sm text-neutral-500"
                                    data-testid="tva-empty" colSpan={8}>
                                    {i18n.t("reports.l.No_months_in_the_selected_window", "No months in the selected window.")}
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => {
                            const hasT = !!r.has_target;
                            const nsSt = r.status.net_sales_customers_status;
                            const fcSt = r.status.food_cost_kate_status;
                            const wSt  = r.status.waste_status;
                            const tid  = `tva-row-${r.restaurant_id}-${r.month}`;
                            return (
                            <tr key={`${r.restaurant_id}-${r.month}`} data-testid={tid}>
                                <td className="px-4 py-3 font-medium whitespace-nowrap">{r.restaurant_name}</td>
                                <td className="px-4 py-3 whitespace-nowrap" data-testid={`${tid}-month`}>{compactMonth(r.month)}</td>
                                {metricCell(r.net_sales,      "eur", "higher", nsSt, hasT, `${tid}-net-sales`)}
                                {metricCell(r.food_cost_pct,  "pct", "lower",  fcSt, hasT, `${tid}-food-cost`)}
                                {metricCell(r.kate_pct,       "pct", "higher", fcSt, hasT, `${tid}-kate`)}
                                {metricCell(r.waste_pct,      "pct", "lower",  wSt,  hasT, `${tid}-waste`)}
                                {metricCell(r.customers,      "int", "higher", nsSt, hasT, `${tid}-customers`)}
                                <td className="px-4 py-3 text-[11px]">
                                    {r.status.no_target
                                        ? statusPill("no_target")
                                        : (r.status.food_cost_kate_status !== "complete"
                                            ? statusPill(r.status.food_cost_kate_status)
                                            : (r.status.net_sales_customers_status !== "complete"
                                                ? statusPill(r.status.net_sales_customers_status)
                                                : statusPill("complete")))}
                                </td>
                            </tr>);
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function CorporateReport({ renderActions } = {}) {
    // Block 3 · Corporate Dashboard. Central filter state drives the
    // KPI Overview, Performance Trend, and Restaurant Comparison
    // sections via the authoritative `/api/corporate/dashboard`
    // endpoint. Block 1 manual entry remains at the bottom.
    const { user } = useApp();
    const isCorporate = user?.role === "corporate";
    const [rid, setRid] = useState(isCorporate ? "all" : (user?.restaurant_id || ""));
    const [from, setFrom] = useState(() => isoBackDays(29));
    const [to, setTo] = useState(() => todayISO());
    const [grouping, setGrouping] = useState("daily");
    const [comparison, setComparison] = useState("none");
    const [metric, setMetric] = useState("net_sales");
    const [detailTab, setDetailTab] = useState("inventory");
    const [restaurants, setRestaurants] = useState([]);
    const [dash, setDash] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    // Stable restaurant color identity — kept in one place for every
    // Corporate chart, legend, and table indicator.
    const colorMap = useRestaurantColors(restaurants);
    const singleColor = rid && rid !== "all" ? colorMap[rid] : null;

    useEffect(() => {
        api.get("/restaurants")
            .then(({ data }) => setRestaurants(data || []))
            .catch(() => setRestaurants([]));
    }, []);

    useEffect(() => {
        if (!from || !to) { setDash(null); return; }
        setLoading(true); setErr(null);
        const params = { from, to, grouping, comparison,
                            restaurant_id: rid || "all" };
        api.get("/corporate/dashboard", { params })
            .then(({ data }) => setDash(data))
            .catch((e) => { setDash(null);
                setErr(detailOr(e?.response?.data?.detail, i18n.t("reports.l.Failed_to_load", "Failed to load"))); })
            .finally(() => setLoading(false));
    }, [rid, from, to, grouping, comparison]);

    // Visual-only: reserve the exact grid-slot space of every
    // hover-enabled panel, and publish the bounding box of the
    // Corporate content area so the hover overlay centres inside the
    // visible report region (sidebar excluded). Re-measures on
    // resize, scroll and whenever the dashboard payload changes.
    useEffect(() => {
        const root = document.querySelector('[data-testid="report-corporate-comparison"]');
        if (!root) return undefined;
        const measure = () => {
            const slots = root.querySelectorAll(".corp-hoverable");
            slots.forEach((el) => {
                if (!document.body.contains(el)) return;
                if (el.matches(":hover")) return;
                const h = el.offsetHeight;
                const w = el.offsetWidth;
                const parent = el.parentElement;
                if (parent && h > 0) {
                    parent.style.minHeight = h + "px";
                    parent.style.minWidth = w + "px";
                }
            });
            const rect = root.getBoundingClientRect();
            root.style.setProperty("--corp-view-left", rect.left + "px");
            root.style.setProperty("--corp-view-width", rect.width + "px");
        };
        const raf = window.requestAnimationFrame(measure);
        const ro = new ResizeObserver(measure);
        ro.observe(root);
        const onResize = () => measure();
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onResize, true);
        return () => {
            window.cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onResize, true);
        };
    }, [dash]);

    const [filtersOpen, setFiltersOpen] = useState(false);
    // Click-to-enlarge: the currently promoted panel's key (one at
    // a time). `null` means no panel is enlarged. Opened by clicking
    // a panel's non-interactive area; closed by outside-click or by
    // pressing Escape (hover does nothing).
    const [enlargedKey, setEnlargedKey] = useState(null);
    const INTERACTIVE_SELECTOR =
        'button, a, select, input, textarea, label, option,' +
        ' [role="button"], [role="combobox"], [role="listbox"],' +
        ' [role="tab"], [role="menuitem"], [contenteditable="true"]';
    const openPanel = (key, e) => {
        if (e && e.target && e.target.closest &&
            e.target.closest(INTERACTIVE_SELECTOR)) return;
        if (enlargedKey === key) return;
        setEnlargedKey(key);
    };
    useEffect(() => {
        if (!enlargedKey) return undefined;
        const onDoc = (e) => {
            const el = document.querySelector(
                `[data-corp-hover-key="${enlargedKey}"]`);
            if (el && !el.contains(e.target)) setEnlargedKey(null);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setEnlargedKey(null);
        };
        document.addEventListener("mousedown", onDoc, true);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc, true);
            document.removeEventListener("keydown", onKey);
        };
    }, [enlargedKey]);
    const activeSummary = (() => {
        const opts = { day: "2-digit", month: "short", year: "numeric" };
        let range = "—";
        try {
            if (from && to) {
                const f = new Date(from).toLocaleDateString(i18n.language === "fi" ? "fi-FI" : "en-GB", opts);
                const t = new Date(to).toLocaleDateString(i18n.language === "fi" ? "fi-FI" : "en-GB", opts);
                range = `${f} – ${t}`;
            }
        } catch {}
        const g = { daily: i18n.t("reports.l.Daily", "Daily"), weekly: i18n.t("reports.l.Weekly", "Weekly"), monthly: i18n.t("reports.l.Monthly", "Monthly") }[grouping] || grouping;
        const c = { none: i18n.t("reports.l.No_comparison", "No comparison"), previous_period: i18n.t("reports.l.vs_Prev_period", "vs Prev period"), previous_year: i18n.t("reports.l.vs_Prev_year", "vs Prev year") }[comparison] || comparison;
        return `${range} · ${g} · ${c}`;
    })();
    // UX cleanup: the Sales & Cost detail tab has been removed from
    // the detail-tab bar — its 12 KPI metrics now render as the only
    // top-level KPI block on the page, its two charts (Performance
    // Trend + Restaurant Comparison bar) sit together with the Net
    // Sales trend chart in the chart row, and its grouping-driven
    // period breakdown lives at the bottom of the main spine. The
    // underlying components, data flow and testids are unchanged.
    const DETAIL_TABS = [
        { key: "inventory",  label: i18n.t("reports.l.Inventory_COGS", "Inventory / COGS") },
        { key: "waste",      label: i18n.t("reports.l.Waste", "Waste") },
        { key: "labour",     label: i18n.t("reports.l.Labour", "Labour") },
        { key: "suppliers",  label: i18n.t("reports.l.Suppliers", "Suppliers") },
        { key: "tva",        label: i18n.t("reports.l.Target_vs_Actual", "Target vs Actual") },
        { key: "targets",    label: i18n.t("reports.l.Targets", "Targets") },
    ];
    return (
        <div data-testid="report-corporate-comparison"
             className="max-w-[1680px] mx-auto">
            {/* Compact top row — period/filter controls + CSV/Excel actions. */}
            <header className="flex flex-wrap items-center justify-between gap-3 mb-3"
                     data-testid="corp-header">
                <div className="flex flex-wrap items-center gap-2"
                     data-testid="corp-header-controls">
                    <div className="inline-flex items-center rounded-full bg-neutral-100 p-1 gap-0.5"
                         data-testid="corp-quick-presets">
                        <button type="button"
                            data-testid="corp-filter-current-month"
                            onClick={() => { setFrom(firstOfCurrentMonthISO()); setTo(todayISO()); }}
                            className="h-8 px-3 rounded-full text-[12.5px] font-medium text-neutral-700 hover:bg-white hover:shadow-sm transition-colors">
                            {i18n.t("reports.currentMonth", "Current month")}
                        </button>
                    </div>
                    <button type="button"
                        onClick={() => setFiltersOpen((v) => !v)}
                        aria-expanded={filtersOpen}
                        data-testid="corp-filters-toggle"
                        className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full border border-neutral-200 text-[12.5px] font-medium text-neutral-700 hover:bg-neutral-50">
                        More filters <span aria-hidden="true">{filtersOpen ? "▴" : "▾"}</span>
                    </button>
                    <span className="text-[11.5px] text-neutral-500 tabular-nums ml-1"
                          data-testid="corp-active-filters-summary">
                        {activeSummary}
                    </span>
                </div>
                <div className="shrink-0" data-testid="corp-header-actions">
                    {renderActions ? renderActions({
                        from, to,
                        restaurant_id: rid || "all",
                    }) : null}
                </div>
            </header>
            {/* Restaurant chips directly below the top row. */}
            <CorporateFilters
                rid={rid} setRid={setRid}
                from={from} setFrom={setFrom} to={to} setTo={setTo}
                grouping={grouping} setGrouping={setGrouping}
                comparison={comparison} setComparison={setComparison}
                restaurants={restaurants}
                colorMap={colorMap}
                disabledAll={!isCorporate}
                open={filtersOpen} setOpen={setFiltersOpen} />
            {loading && <div className="text-sm text-neutral-500 mb-3" data-testid="corp-dash-loading">{i18n.t("common.loading", "Loading…")}</div>}
            {err && <div className="text-sm text-rose-600 mb-3" data-testid="corp-dash-error">{String(err)}</div>}
            {/* KPI Overview — primary 6-tile grid is hidden (visual cleanup;
                see KpiOverview). Coverage / missing-data warnings and
                sr-only testid mirrors remain in the DOM so they stay
                visible and tests keep passing. */}
            <KpiOverview dash={dash} restaurantColor={singleColor} />
            {/* Sales & Cost Analysis — the ONLY KPI block, 12 metrics. */}
            <SalesCostAnalysis dash={dash} variant="kpis-only" />
            {/* Existing charts together: Net Sales trend, Performance Trend
                and Restaurant Comparison bar chart. Controls / data / tests
                unchanged — only the layout grouping changed. Each panel
                is CLICK-to-enlarge (`.corp-hoverable` + `data-corp-hover-key`):
                clicking a panel's non-interactive area promotes it to a
                centred landscape overlay via `.is-enlarged`; clicks on
                dropdowns / links / buttons keep their original action.
                Only one panel is enlarged at a time. Close by clicking
                outside or pressing Escape. */}
            <section className="mt-2 mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4 corp-hoverable-row"
                     data-testid="corp-charts-row">
                <div className="min-w-0 flex">
                    <div className={"w-full corp-hoverable" + (enlargedKey === "nsd" ? " is-enlarged" : "")}
                         data-corp-hover-key="nsd"
                         data-hover-origin="top-left" data-hover-size="chart"
                         onClick={(e) => openPanel("nsd", e)}>
                        <NetSalesByDayChart dash={dash} colorMap={colorMap} selectedRid={rid} />
                    </div>
                </div>
                <div className="min-w-0 flex">
                    <div className={"w-full corp-hoverable" + (enlargedKey === "pt" ? " is-enlarged" : "")}
                         data-corp-hover-key="pt"
                         data-hover-size="chart"
                         onClick={(e) => openPanel("pt", e)}>
                        <PerformanceTrend dash={dash} metric={metric} setMetric={setMetric}
                            selectedRid={rid} colorMap={colorMap} />
                    </div>
                </div>
                <div className="min-w-0 flex">
                    <div className={"w-full corp-hoverable" + (enlargedKey === "rc" ? " is-enlarged" : "")}
                         data-corp-hover-key="rc"
                         data-hover-origin="top-right" data-hover-size="chart"
                         onClick={(e) => openPanel("rc", e)}>
                        <RestaurantComparisonChart dash={dash} colorMap={colorMap} />
                    </div>
                </div>
            </section>
            {/* Full-width Restaurant Comparison table. Not click-enlargeable
                per spec — it already uses the full viewport width. */}
            <RestaurantComparisonTable dash={dash} colorMap={colorMap} />
            {/* Top Waste Items + Recent Inventory Counts side by side.
                Same click-to-enlarge behaviour as the chart row. */}
            <section className="mt-5 mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 corp-hoverable-row"
                     data-testid="corp-waste-inventory-row">
                <div className="min-w-0 flex">
                    <div className={"w-full corp-hoverable" + (enlargedKey === "tw" ? " is-enlarged" : "")}
                         data-corp-hover-key="tw"
                         data-hover-origin="top-left" data-hover-size="info"
                         onClick={(e) => openPanel("tw", e)}>
                        <TopWasteItemsPanel rid={rid} from={from} to={to} />
                    </div>
                </div>
                <div className="min-w-0 flex">
                    <div className={"w-full corp-hoverable" + (enlargedKey === "ri" ? " is-enlarged" : "")}
                         data-corp-hover-key="ri"
                         data-hover-origin="top-right" data-hover-size="info"
                         onClick={(e) => openPanel("ri", e)}>
                        <RecentInventoryCountsPanel
                            restaurants={restaurants}
                            colorMap={colorMap}
                            selectedRid={rid}
                            from={from} to={to} />
                    </div>
                </div>
            </section>
            {/* Period breakdown at the bottom (Daily / Weekly / Monthly
                label follows the selected grouping). */}
            <SalesCostAnalysis dash={dash} variant="period-only" />
            {/* Detail tab bar — every remaining section stays reachable and
                stays MOUNTED across tab switches so unsaved inputs (e.g.
                Targets form) and internal component state persist. The
                "Sales & Cost" tab has been removed from this bar because
                its content now lives in the main spine above and must not
                be duplicated. Only the visibility of each body toggles. */}
            <section className="mt-8" data-testid="corp-details">
                <div role="tablist"
                     className="flex flex-wrap gap-1 border-b border-neutral-200 mb-5 overflow-x-auto"
                     data-testid="corp-details-tabs">
                    {DETAIL_TABS.map((t) => {
                        const active = detailTab === t.key;
                        return (
                            <button key={t.key} type="button" role="tab"
                                aria-selected={active}
                                data-testid={`corp-details-tab-${t.key}`}
                                onClick={() => setDetailTab(t.key)}
                                className={
                                    "h-9 px-3 -mb-px border-b-2 text-[13px] font-medium transition-colors " +
                                    (active
                                        ? "border-neutral-900 text-neutral-900"
                                        : "border-transparent text-neutral-500 hover:text-neutral-800")
                                }>
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                <div data-testid={`corp-details-body-${detailTab}`}>
                    <div hidden={detailTab !== "inventory"} data-testid="corp-details-pane-inventory">
                        <InventoryCogsAnalysis dash={dash} />
                    </div>
                    <div hidden={detailTab !== "waste"} data-testid="corp-details-pane-waste">
                        <WasteAnalysis dash={dash} />
                    </div>
                    <div hidden={detailTab !== "labour"} data-testid="corp-details-pane-labour">
                        <LabourAnalysis dash={dash} />
                    </div>
                    <div hidden={detailTab !== "suppliers"} data-testid="corp-details-pane-suppliers">
                        <SupplierAnalysis dash={dash} />
                    </div>
                    <div hidden={detailTab !== "tva"} data-testid="corp-details-pane-tva">
                        <TargetVsActual dash={dash} />
                    </div>
                    <div hidden={detailTab !== "targets"} data-testid="corp-details-pane-targets">
                        <TargetsPanel restaurants={restaurants} />
                    </div>
                </div>
            </section>
        </div>
    );
}
